import { BarChartOutlined, CopyOutlined, DeleteOutlined, DownloadOutlined, DownOutlined, EditOutlined, EyeOutlined, FilePdfOutlined, LinkOutlined, LoadingOutlined, PlusOutlined, UpOutlined } from '@ant-design/icons';
import { Alert, Badge, Button, Drawer, Form, Image, Input, InputNumber, List, message, Modal, Pagination, Popconfirm, Select, Space, Switch, Tabs, Tag, Upload } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { DataService } from '../services/data';
import { AuthService } from '../services/auth';
import { SocketService } from '../services/socket';
import { ImageComposer } from '../utils/imageComposer';
import { PdfComposer } from '../utils/pdfComposer';
import { formatFullWidthPunctuation } from '../utils/textFormatter';
// 移除 Root 端单独的用户管理与邀请码管理入口
import OrganizationManagement from './OrganizationManagement';
import OrganizationOverview from './OrganizationOverview';

// 导入新的统一模板系统
import {
  MAX_TAROT_CARD_COUNT,
  TAROT_INTERPRETATION_MODEL,
  TAROT_INTERPRETER_LABEL,
  formatSeasonAnchorDate,
  getSeasonContext,
  initializeTemplates,
  parseTarotCardCount,
  TemplateManager,
  TEMPLATE_IDS
} from '@data-management/shared';
import { FrontendTemplateAdapter, Template } from '../adapters/templateAdapter';

/**
 * 获取UTC+8时区的当前时间
 * @returns UTC+8时区的Date对象
 */
const getUTC8Time = (): Date => {
  const now = new Date();
  // 获取UTC时间，然后加上8小时得到UTC+8时间
  return new Date(now.getTime() + (now.getTimezoneOffset() * 60 * 1000) + (8 * 60 * 60 * 1000));
};

/**
 * 将UTC+8时间格式化为datetime-local输入格式
 * @param date UTC+8时区的Date对象
 * @returns YYYY-MM-DDTHH:mm 格式字符串
 */
const formatUTC8DateForInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * 获取默认的统计日期范围（基于UTC+8时区）
 * 返回相邻两天的05:00，确保覆盖当前时刻
 * @returns [开始时间字符串, 结束时间字符串]
 */
const getDefaultDateRange = (): [string, string] => {
  const utc8Now = getUTC8Time();
  const currentHour = utc8Now.getHours();

  let startDate: Date;
  let endDate: Date;

  if (currentHour >= 5) {
    // 如果当前UTC+8时间是05:00之后，统计今天05:00到明天05:00
    startDate = new Date(utc8Now.getFullYear(), utc8Now.getMonth(), utc8Now.getDate(), 5, 0, 0);
    endDate = new Date(utc8Now.getFullYear(), utc8Now.getMonth(), utc8Now.getDate() + 1, 5, 0, 0);
  } else {
    // 如果当前UTC+8时间是05:00之前，统计昨天05:00到今天05:00
    startDate = new Date(utc8Now.getFullYear(), utc8Now.getMonth(), utc8Now.getDate() - 1, 5, 0, 0);
    endDate = new Date(utc8Now.getFullYear(), utc8Now.getMonth(), utc8Now.getDate(), 5, 0, 0);
  }

  return [formatUTC8DateForInput(startDate), formatUTC8DateForInput(endDate)];
};

/**
 * 将datetime-local格式转换为后端需要的UTC时间格式
 * @param datetimeLocal YYYY-MM-DDTHH:mm 格式（用户输入的UTC+8时间）
 * @returns YYYY-MM-DD HH:mm:ss 格式的UTC时间字符串
 */
const formatDateTimeForAPI = (datetimeLocal: string): string => {
  if (!datetimeLocal) return '';

  // 解析用户输入的时间（这是UTC+8时间）
  const [datePart, timePart] = datetimeLocal.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);

  // 直接使用 Date.UTC 创建UTC时间，然后减去8小时得到正确的UTC时间
  // 因为用户输入的是UTC+8时间，我们需要减去8小时来得到对应的UTC时间
  const utcTime = Date.UTC(year, month - 1, day, hours, minutes, 0) - (8 * 60 * 60 * 1000);
  const utcDate = new Date(utcTime);

  // 格式化为 YYYY-MM-DD HH:mm:ss 格式
  const utcYear = utcDate.getUTCFullYear();
  const utcMonth = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
  const utcDay = String(utcDate.getUTCDate()).padStart(2, '0');
  const utcHours = String(utcDate.getUTCHours()).padStart(2, '0');
  const utcMinutes = String(utcDate.getUTCMinutes()).padStart(2, '0');
  const utcSeconds = String(utcDate.getUTCSeconds()).padStart(2, '0');

  return `${utcYear}-${utcMonth}-${utcDay} ${utcHours}:${utcMinutes}:${utcSeconds}`;
};

/**
 * 安全地解析记录数据
 * @param dataString JSON字符串
 * @returns 解析后的对象，如果解析失败返回空对象
 */
const safeParseRecordData = (dataString: string): any => {
  try {
    return JSON.parse(dataString);
  } catch (error) {
    console.error('解析记录数据失败:', error, '原始数据:', dataString);
    return {};
  }
};

/**
 * 生成记录内容的哈希值（用于判断图片是否需要重新生成）
 * @param record 数据记录
 * @param useThinking 是否使用深度思考结果
 * @returns 内容哈希字符串
 */
const generateContentHash = (record: DataRecord, useThinking: boolean): string => {
  const data = safeParseRecordData(record.data);
  const llmResult = useThinking ? record.llmThinkingResult : record.llmResult;
  const formattedLlmResult = formatFullWidthPunctuation(llmResult);
  const tarotImageSource = record.tarotImageUrl || record.tarotImagePath;
  
  // 影响图片生成的关键内容
  const contentForHash = {
    title: data.title || '',
    llmResult: formattedLlmResult,
    tarotImageSource: tarotImageSource || '',
    templateId: data.templateId || '',
    useThinking
  };
  
  // 简单的字符串哈希（生产环境可以使用更好的哈希算法）
  const str = JSON.stringify(contentForHash);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // 转换为32位整数
  }
  return Math.abs(hash).toString(16);
};

const sanitizeFileNameSegment = (value: string): string => {
  if (!value) {
    return '';
  }
  return value.replace(/[\\/:*?"<>|]/g, '').trim();
};

const buildPdfFileName = (record: DataRecord, recordData: any): string => {
  const recordDate = record?.createdAt ? new Date(record.createdAt) : undefined;
  const seasonContext = getSeasonContext(recordDate);
  const anchorDate = formatSeasonAnchorDate(seasonContext.anchorDate);
  const baseName = `${anchorDate} ${seasonContext.term}四季牌.pdf`;
  if (!recordData || typeof recordData !== 'object') {
    return baseName;
  }

  if (recordData.templateId === TEMPLATE_IDS.FOUR_SEASONS_SPREAD) {
    const sanitizedName = sanitizeFileNameSegment(recordData.customerName);
    if (sanitizedName) {
      return `${anchorDate} ${sanitizedName} ${seasonContext.term}四季牌.pdf`;
    }
  }

  return baseName;
};

const CUSTOMER_DESCRIPTION_SNIPPETS = [
  '之前在一起的时候',
  '令我开心的事情是',
  '令我难过的事情是',
  '令我生气的事情是',
  '暗示过对方我喜欢对方',
  '最大的矛盾就是'
];

/**
 * 检查指定记录和类型的图片是否正在生成中
 * @param record 数据记录
 * @param useThinking 是否使用深度思考结果
 * @param generatingSet 生成中的缓存键集合
 * @returns 是否正在生成中
 */
const isGeneratingCompositeImage = (record: DataRecord, useThinking: boolean, generatingSet: Set<string>): boolean => {
  const contentHash = generateContentHash(record, useThinking);
  const compositeKey = `${record.id}_${useThinking ? 'thinking' : 'normal'}_${contentHash}`;
  return generatingSet.has(compositeKey);
};

const isGeneratingPdfDocument = (record: DataRecord, useThinking: boolean, generatingSet: Set<string>): boolean => {
  const contentHash = generateContentHash(record, useThinking);
  const pdfKey = `${record.id}_${useThinking ? 'thinking' : 'normal'}_${contentHash}_pdf`;
  return generatingSet.has(pdfKey);
};

interface User {
  id: number;
  username: string;
  role: 'worker' | 'admin' | 'controller';
  organizationId?: number;
}

interface DataRecord {
  id: number;
  userId: number;
  username?: string;
  organizationName?: string;
  data: string;
  llmResult?: string;
  llmThinkingResult?: string;
  llmCallCount?: number; // LLM调用次数
  tarotImagePath?: string; // 兼容旧格式
  tarotImageUrl?: string; // Cloudinary URL
  tarotImagePublicId?: string; // Cloudinary Public ID
  tarotImageUploadStatus?: string; // 上传状态: 'uploading' | 'completed' | 'failed'
  generatedImagePath?: string; // 兼容旧格式
  generatedImageUrl?: string; // Cloudinary URL
  generatedImagePublicId?: string; // Cloudinary Public ID
  isDelivered?: boolean;
  isDraft?: boolean;
  parentRecordId?: number; // 父订单ID（关联订单功能）
  parentResultType?: string; // 父订单结果类型：'normal' | 'thinking'
  createdAt: string;
  updatedAt: string;
}

interface DashboardProps {
  user: User;
  onLogout?: () => void;
  onSessionExpired?: () => void;
}


interface StatisticsResult {
  orderCount: number;
  totalCards: number;
  totalCardTimes: number; // 总牌次（牌数 × 调用次数）
  cardCountGroups: { cardCount: number; orderCount: number }[];
}

/**
 * 将日期格式化为 UTC+8 时区的本地时间字符串
 * @param dateString ISO 日期字符串
 * @returns 格式化后的中文日期时间字符串
 */
const formatDateToUTC8 = (dateString: string): string => {
  // 检查输入是否有效
  if (!dateString || typeof dateString !== 'string') {
    console.warn('formatDateToUTC8: 无效的日期字符串:', dateString);
    return '时间格式错误';
  }

  // 尝试解析日期
  const date = new Date(dateString);

  // 检查日期是否有效
  if (isNaN(date.getTime())) {
    console.warn('formatDateToUTC8: 无法解析的日期字符串:', dateString);
    return '时间格式错误';
  }

  try {
    // 使用 Asia/Shanghai 时区格式化日期，自动转换为 UTC+8
    return date.toLocaleString('zh-CN', {
      timeZone: 'Asia/Shanghai',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  } catch (error) {
    console.error('formatDateToUTC8: 格式化日期时出错:', error, '原始字符串:', dateString);
    return '时间格式错误';
  }
};

// 图片上传组件
interface ImageUploadFormProps {
  onSubmit: (file: File, orderNumber: string, model: string) => void;
  onBack: () => void;
  processing: boolean;
}

const ImageUploadForm: React.FC<ImageUploadFormProps> = ({ onSubmit, onBack, processing }) => {
  const [form] = Form.useForm();
  // 监听“自动抽牌”选项
  const autoDraw = Form.useWatch?.('autoDraw', form);
  const [fileList, setFileList] = useState<any[]>([]);

  const handleFinish = (values: { orderNumber: string; model: string }) => {
    if (fileList.length === 0) {
      message.error('请上传图片');
      return;
    }
    
    const file = fileList[0].originFileObj;
    onSubmit(file, values.orderNumber, values.model);
  };

  const uploadProps = {
    beforeUpload: (file: File) => {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error('只能上传图片文件!');
        return false;
      }
      
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error('图片大小不能超过10MB!');
        return false;
      }
      
      return false; // 不自动上传
    },
    onChange: (info: any) => {
      setFileList(info.fileList.slice(-1)); // 只保留最后一个文件
    },
    fileList,
  };

  return (
    <div style={{ padding: '40px 20px' }}>
      <h3 style={{ textAlign: 'center', marginBottom: '30px', color: '#333' }}>图片录入</h3>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        style={{ maxWidth: '400px', margin: '0 auto' }}
      >
        <Form.Item
          label="订单号"
          name="orderNumber"
          rules={[{ required: true, message: '请输入订单号' }]}
        >
          <Input placeholder="请输入订单号" />
        </Form.Item>

        <Form.Item
          label="识别模型"
          name="model"
          initialValue={TAROT_INTERPRETATION_MODEL}
          rules={[{ required: true, message: '请选择识别模型' }]}
        >
          <Select placeholder="请选择识别模型">
            <Select.Option value={TAROT_INTERPRETATION_MODEL}>{TAROT_INTERPRETER_LABEL}</Select.Option>
          </Select>
        </Form.Item>
        
        <Form.Item
          label="上传聊天截图"
          required
        >
          <Upload.Dragger {...uploadProps}>
            <p className="ant-upload-drag-icon">
              <UpOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
            </p>
            <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
            <p className="ant-upload-hint">
              支持单个上传，支持JPG、PNG等图片格式，大小不超过10MB
            </p>
          </Upload.Dragger>
        </Form.Item>
        
        <div style={{ textAlign: 'center', marginTop: '30px' }}>
          <Space>
            <Button onClick={onBack}>返回</Button>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={processing}
              disabled={fileList.length === 0}
            >
              {processing ? '识别中...' : '开始识别'}
            </Button>
          </Space>
        </div>
      </Form>
      
      <div style={{ marginTop: '40px', padding: '20px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
        <h4>使用说明：</h4>
        <ul style={{ marginLeft: '20px', color: '#666' }}>
          <li>上传客户咨询的聊天截图</li>
          <li>系统会自动识别图片中的文字信息</li>
          <li>识别后可以编辑修改信息，然后添加塔罗牌阵</li>
        </ul>
      </div>
    </div>
  );
};

const Dashboard: React.FC<DashboardProps> = ({ user, onLogout, onSessionExpired }) => {
  const [records, setRecords] = useState<DataRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0
  });
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<DataRecord | null>(null);
  const [formValid, setFormValid] = useState(false);
  const [showTemplateSelection, setShowTemplateSelection] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [showInputTypeSelection, setShowInputTypeSelection] = useState(false);
  const [showImageInput, setShowImageInput] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [extractedData, setExtractedData] = useState<any>(null);
  const [processingImage, setProcessingImage] = useState(false);
  const [cardCount, setCardCount] = useState<number>(3);
  const [formResetKey, setFormResetKey] = useState<number>(0); // 用于强制重置表单字段
  const [form] = Form.useForm();
  const isRootUser = user.username === 'root';
  const usesRootDefaults = isRootUser || user.id === 1 || user.organizationId === 8;
  // 监听“自动抽牌”选项（用于控制相关表单项的禁用/必填逻辑）
  const autoDraw = Form.useWatch?.('autoDraw', form);
  const customerDescriptionRef = useRef<any>(null);
  const customerDescriptionSelectionRef = useRef<{ start: number; end: number } | null>(null);

  // 新增状态变量
  const [searchValue, setSearchValue] = useState<string>('');
  const [expandedRecords, setExpandedRecords] = useState<Set<number>>(new Set());
  const [relatedOrderData, setRelatedOrderData] = useState<any>(null); // 关联订单临时数据
  const recordRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  const editingTemplateId = React.useMemo(() => {
    if (!editingRecord) {
      return undefined;
    }
    const data = safeParseRecordData(editingRecord.data);
    return data.templateId as string | undefined;
  }, [editingRecord]);

  const hasStoredAutomaticDraw = React.useMemo(() => {
    if (!editingRecord) {
      return false;
    }
    const data = safeParseRecordData(editingRecord.data);
    return !!(data.autoDraw && Array.isArray(data.drawnCards) && data.drawnCards.length > 0);
  }, [editingRecord]);

  // 调试：监听form实例变化
  useEffect(() => {
    if (form) {
      const values = form.getFieldsValue();
      console.log('🔍 Form初始化时的值:', values);
    }
  }, [form]);


  // 图片相关状态
  const [tarotImageFile, setTarotImageFile] = useState<any>(null);
  const [imagePreviewVisible, setImagePreviewVisible] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  // 前端合成图相关状态
  const [compositeImages, setCompositeImages] = useState<Map<string, Blob>>(new Map());
  const [generatingComposite, setGeneratingComposite] = useState<Set<string>>(new Set());
  const [compositePdfs, setCompositePdfs] = useState<Map<string, Blob>>(new Map());
  const [generatingPdf, setGeneratingPdf] = useState<Set<string>>(new Set());
  const [contentHashes, setContentHashes] = useState<Map<number, string>>(new Map()); // 记录每个记录的内容哈希
  // 正在等待LLM生成/重新解读的记录集合
  const [llmGenerating, setLlmGenerating] = useState<Set<number>>(new Set());

  // 编辑AI分析结果相关状态
  const [editLLMDrawerVisible, setEditLLMDrawerVisible] = useState(false);
  const [editingLLMRecord, setEditingLLMRecord] = useState<DataRecord | null>(null);
  const [editingThinkingResult, setEditingThinkingResult] = useState(false); // 标记是否编辑深度思考结果
  const [llmEditForm] = Form.useForm();

  // 统计功能相关状态
  const [statisticsModalVisible, setStatisticsModalVisible] = useState(false);
  const [statisticsLoading, setStatisticsLoading] = useState(false);
  const [statisticsResult, setStatisticsResult] = useState<string>('');
  const [statisticsForm] = Form.useForm();
  const [csvExportLoading, setCsvExportLoading] = useState(false);

  const updateCustomerDescriptionSelection = (event: React.SyntheticEvent<HTMLTextAreaElement>) => {
    const target = event.currentTarget;
    if (typeof target.selectionStart === 'number' && typeof target.selectionEnd === 'number') {
      customerDescriptionSelectionRef.current = {
        start: target.selectionStart,
        end: target.selectionEnd
      };
    }
  };

  const focusCustomerDescription = (cursorPosition?: number) => {
    const textarea = customerDescriptionRef.current?.resizableTextArea?.textArea as HTMLTextAreaElement | undefined;
    if (!textarea) {
      return;
    }
    textarea.focus();
    if (cursorPosition !== undefined) {
      textarea.setSelectionRange(cursorPosition, cursorPosition);
      customerDescriptionSelectionRef.current = { start: cursorPosition, end: cursorPosition };
    }
  };

  const replaceCustomerDescriptionPronoun = (from: string, to: string) => {
    const currentValue = form.getFieldValue('customerDescription') || '';
    if (!currentValue) {
      return;
    }
    form.setFieldsValue({ customerDescription: currentValue.split(from).join(to) });
    window.requestAnimationFrame(() => {
      focusCustomerDescription();
    });
  };

  const insertCustomerDescriptionSnippet = (snippet: string) => {
    const currentValue = form.getFieldValue('customerDescription') || '';
    const selection = customerDescriptionSelectionRef.current;
    const start = selection ? selection.start : currentValue.length;
    const end = selection ? selection.end : currentValue.length;
    const safeStart = Math.max(0, Math.min(start, currentValue.length));
    const safeEnd = Math.max(0, Math.min(end, currentValue.length));
    const nextValue = `${currentValue.slice(0, safeStart)}${snippet}${currentValue.slice(safeEnd)}`;
    const cursorPosition = safeStart + snippet.length;
    form.setFieldsValue({ customerDescription: nextValue });
    window.requestAnimationFrame(() => {
      focusCustomerDescription(cursorPosition);
    });
  };

  const preventTextAreaBlur = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
  };

  // 初始化统一模板系统
  React.useEffect(() => {
    initializeTemplates();
  }, []);

  // 基础字段定义 - 使用统一模板系统
  const getDataFields = (template: Template | null) => {
    const templateIdFromForm = form?.getFieldValue?.('templateId');
    const effectiveTemplateId = template?.id || templateIdFromForm || editingTemplateId || null;
    const effectiveTemplate = template || (effectiveTemplateId ? ({ id: effectiveTemplateId } as Template) : null);

    // 获取模板相关的字段配置
    const fieldConfig = FrontendTemplateAdapter.getFormFieldConfig(effectiveTemplate);
    const isFourSeasonsTemplate = effectiveTemplateId === TEMPLATE_IDS.FOUR_SEASONS_SPREAD;

    const customerNameDefault = fieldConfig.customerNameDefaultValue !== undefined
      ? fieldConfig.customerNameDefaultValue
      : (isFourSeasonsTemplate ? undefined : '');

    const interpretationOptions = [
      { label: TAROT_INTERPRETER_LABEL, value: TAROT_INTERPRETATION_MODEL, disabled: false }
    ];

    const baseFields = [
      {
        name: 'orderNumber',
        label: '订单号',
        required: true,
        type: 'input' as const
      },
      {
        name: 'title',
        label: '标题',
        required: true,
        type: 'input' as const,
        defaultValue: fieldConfig.titleDefaultValue || '',
        disabled: fieldConfig.titleDisabled
      },
      {
        name: 'customerName',
        label: '顾客姓名',
        required: isFourSeasonsTemplate,
        type: 'input' as const,
        defaultValue: customerNameDefault,
        disabled: isFourSeasonsTemplate ? false : (fieldConfig.customerNameDisabled ?? true),
        placeholder: '请输入顾客姓名'
      },
      {
        name: 'customerInfo',
        label: '客户主体及客户语境中的客体信息',
        required: true,
        type: 'textarea' as const,
        placeholder: '占卜者性别：女\n对方性别：男\n占卜者年龄：22\n对方年龄：22\n现在是什么关系：分手\n是否在正常联系：否'
      },
      {
        name: 'customerDescription',
        label: '客户自述',
        required: true,
        type: 'textarea' as const,
        placeholder: '我和他是在星币乐园认识的。'
      },
      {
        name: 'customerQuestion',
        label: '客户想问的问题',
        required: true,
        type: 'textarea' as const,
        defaultValue: fieldConfig.customerQuestionDefaultValue || '',
        disabled: fieldConfig.customerQuestionDisabled
      },
      {
        name: 'cardCount',
        label: '客户抽牌数量',
        required: true,
        type: 'number' as const,
        defaultValue: template ? (FrontendTemplateAdapter.getTemplateDefaultValues(template).cardCount || 1) : 1,
        disabled: fieldConfig.cardCountDisabled || hasStoredAutomaticDraw,
        min: 1,
        max: MAX_TAROT_CARD_COUNT
      },
      {
        name: 'generateNormal',
        label: '生成普通分析结果',
        required: false,
        type: 'switch' as const,
        defaultValue: !usesRootDefaults
      },
      {
        name: 'generateThinking',
        label: '生成深度思考分析结果',
        required: false,
        type: 'switch' as const,
        defaultValue: true
      },
      {
        name: 'interpretationModel',
        label: '解读模型',
        required: true,
        type: 'select' as const,
        defaultValue: TAROT_INTERPRETATION_MODEL,
        options: interpretationOptions
      },
      // 新增：自动抽牌选项（默认不选）
      {
        name: 'autoDraw',
        label: '系统自动抽牌',
        required: false,
        type: 'switch' as const,
        defaultValue: false,
        disabled: hasStoredAutomaticDraw
      },
      // 按需求：将塔罗牌阵上传放到“解读模型”和“自动抽牌”之后
      {
        name: 'tarotImage',
        label: '塔罗牌阵',
        required: false,
        type: 'upload' as const,
        placeholder: '请上传塔罗牌阵图片'
      }
    ];

    return baseFields;
  };

  // 模板定义 - 使用统一模板系统
  const templates: Template[] = FrontendTemplateAdapter.getAllFrontendTemplates();

  useEffect(() => {
    loadRecords();
    
    // Listen for LLM results
    SocketService.on('llm-result', (data: { recordId: number; results: { normalResult?: string; thinkingResult?: string }; llmCallCount?: number; quotaInsufficient?: boolean }) => {
      console.log('🔍 收到WebSocket结果:', {
        recordId: data.recordId,
        hasNormalResult: !!data.results.normalResult,
        hasThinkingResult: !!data.results.thinkingResult,
        normalResultLength: data.results.normalResult?.length || 0,
        thinkingResultLength: data.results.thinkingResult?.length || 0
      });

      setRecords(prev => prev.map(record =>
        record.id === data.recordId
          ? {
              ...record,
              llmResult: data.results.normalResult !== undefined ? data.results.normalResult : record.llmResult,
              llmThinkingResult: data.results.thinkingResult !== undefined ? data.results.thinkingResult : record.llmThinkingResult,
              llmCallCount: data.llmCallCount !== undefined ? data.llmCallCount : record.llmCallCount
            }
          : record
      ));
      // 收到任意LLM结果后，认为该记录的“重新解读中”状态可清除
      setLlmGenerating(prev => {
        const next = new Set(prev);
        next.delete(data.recordId);
        return next;
      });
      

      
      // 清理与该记录相关的图片缓存，因为LLM结果已更新
      setCompositeImages(prev => {
        const newMap = new Map(prev);
        for (const key of newMap.keys()) {
          if (key.startsWith(`${data.recordId}_`)) {
            console.log(`🧹 清理过期缓存: ${key}`);
            newMap.delete(key);
          }
        }
        return newMap;
      });
      setCompositePdfs(prev => {
        const newMap = new Map(prev);
        for (const key of newMap.keys()) {
          if (key.startsWith(`${data.recordId}_`)) {
            newMap.delete(key);
          }
        }
        return newMap;
      });
    });

    // Listen for image generation results
    SocketService.on('image-generated', (data: { recordId: number; generatedImagePath: string; imageUrl: string; downloadUrl: string }) => {
      setRecords(prev => prev.map(record =>
        record.id === data.recordId
          ? { ...record, generatedImagePath: data.generatedImagePath }
          : record
      ));
      message.success('合成图片生成完成！');
    });

    // Listen for image upload completion
    SocketService.on('image-upload-completed', (data: { recordId: number; cloudinaryUrl: string; publicId: string; status: string }) => {
      console.log('📨 收到图片上传完成通知:', data);
      
      // 更新记录的图片信息
      setRecords(prevRecords => 
        prevRecords.map(record => 
          record.id === data.recordId 
            ? { 
                ...record, 
                tarotImageUrl: data.cloudinaryUrl,
                tarotImagePublicId: data.publicId,
                tarotImageUploadStatus: data.status
              }
            : record
        )
      );
      
      message.success(`记录 ${data.recordId} 的图片上传到云端完成！`);
    });

    // Listen for image upload failure
    SocketService.on('image-upload-failed', (data: { recordId: number; error: string; status: string }) => {
      console.log('📨 收到图片上传失败通知:', data);
      
      // 更新记录的上传状态
      setRecords(prevRecords => 
        prevRecords.map(record => 
          record.id === data.recordId 
            ? { 
                ...record, 
                tarotImageUploadStatus: data.status
              }
            : record
        )
      );
      
      message.warning(`记录 ${data.recordId} 的图片上传失败: ${data.error}`);
    });

    // Listen for draft creation
    SocketService.on('draft-created', (data: { recordId: number; userId: number; data: any }) => {
      console.log('📝 收到草稿创建通知:', data);
      loadRecords(); // 刷新记录列表
    });
    
    // Listen for extraction completion
    SocketService.on('extraction-completed', (data: { recordId: number; userId: number; data: any; extractedData: any }) => {
      console.log('✅ 收到图片识别完成通知:', data);
      setRecords(prev => prev.map(record =>
        record.id === data.recordId
          ? { ...record, data: JSON.stringify(data.data), isDraft: true }
          : record
      ));
      message.success('图片识别完成，可以编辑记录了！');
    });
    
    // Listen for extraction failure
    SocketService.on('extraction-failed', (data: { recordId: number; userId: number; error: string }) => {
      console.log('❌ 收到图片识别失败通知:', data);
      message.error(`图片识别失败: ${data.error}`);
      loadRecords(); // 刷新记录列表
    });
    
    return () => {
      SocketService.off('llm-result');
      SocketService.off('image-generated');
      SocketService.off('draft-created');
      SocketService.off('extraction-completed');
      SocketService.off('extraction-failed');
    };
  }, []);

  // 组件卸载时清理资源
  useEffect(() => {
    return () => {
      // 清理塔罗牌图片的 URL 对象
      if (tarotImageFile && tarotImageFile.url) {
        URL.revokeObjectURL(tarotImageFile.url);
      }
      // 清理合成图的 URL 对象
      compositeImages.forEach((blob) => {
        if (blob) {
          // 注意：Blob 对象本身不需要手动清理，但如果有相关的 URL 对象需要清理
        }
      });
    };
  }, [tarotImageFile, compositeImages]);

  const loadRecords = async (page = 1, pageSize = 10, search?: string) => {
    setLoading(true);
    try {
      const response = await DataService.getRecords(page, pageSize, search);

      // 开发环境下检查时间格式
      if (response.records && response.records.length > 0) {
        const firstRecord = response.records[0];
        console.log('🔍 时间格式检查:', {
          createdAt: firstRecord.createdAt,
          createdAtType: typeof firstRecord.createdAt,
          parsedDate: new Date(firstRecord.createdAt),
          isValidDate: !isNaN(new Date(firstRecord.createdAt).getTime()),
          formattedResult: formatDateToUTC8(firstRecord.createdAt)
        });
      }

      setRecords(response.records);
      setPagination(response.pagination);
    } catch (error: any) {
      // 401 由全局 axios interceptor 统一处理（清理登录态并提示重新登录）
      if (error.response?.status === 401) {
        return;
      }
      
      message.error('加载数据失败');
      console.error('加载数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 处理页码变化
  const handlePageChange = (page: number, pageSize?: number) => {
    const currentSearch = searchValue.trim() || undefined;
    loadRecords(page, pageSize || pagination.pageSize, currentSearch);
  };

  // 搜索功能 - 调用后端搜索
  const handleSearch = (value: string) => {
    setSearchValue(value);
    const searchTerm = value.trim() || undefined;
    // 重置到第一页进行搜索
    loadRecords(1, pagination.pageSize, searchTerm);
  };

  // 跳转到指定订单
  const scrollToOrder = (orderNumber: string) => {
    const targetRecord = records.find(record => {
      const data = safeParseRecordData(record.data);
      return data.orderNumber === orderNumber;
    });

    if (targetRecord) {
      const element = recordRefs.current[targetRecord.id];
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // 高亮显示
        element.style.backgroundColor = '#fff7e6';
        setTimeout(() => {
          element.style.backgroundColor = '';
        }, 2000);
      }
    }
  };

  // 处理搜索框回车
  const handleSearchEnter = (value: string) => {
    handleSearch(value);
  };

  // 切换订单展开/折叠状态
  const toggleExpanded = (recordId: number) => {
    const newExpanded = new Set(expandedRecords);
    if (newExpanded.has(recordId)) {
      newExpanded.delete(recordId);
    } else {
      newExpanded.add(recordId);
    }
    setExpandedRecords(newExpanded);
  };

  const coerceSwitchValue = (value: unknown, defaultValue: boolean): boolean => {
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'on') {
        return true;
      }
      if (normalized === 'false' || normalized === '0' || normalized === 'no' || normalized === 'off') {
        return false;
      }
      return defaultValue;
    }
    if (typeof value === 'number') {
      if (value === 1) {
        return true;
      }
      if (value === 0) {
        return false;
      }
      return defaultValue;
    }
    if (value === null || value === undefined) {
      return defaultValue;
    }
    return Boolean(value);
  };

  const handleSubmit = async (values: any) => {
    try {
      const finalValues = { ...values };

      const normalizedCardCount = parseTarotCardCount(finalValues.cardCount);
      if (normalizedCardCount === null) {
        message.error(`抽牌数量必须是 1-${MAX_TAROT_CARD_COUNT} 之间的整数`);
        return;
      }
      finalValues.cardCount = normalizedCardCount;

      finalValues.interpretationModel = TAROT_INTERPRETATION_MODEL;

      // Ensure switch fields are explicit booleans so the backend can reliably trigger generation.
      finalValues.generateNormal = coerceSwitchValue(finalValues.generateNormal, !usesRootDefaults);
      finalValues.generateThinking = coerceSwitchValue(finalValues.generateThinking, true);

      if (typeof finalValues.customerName === 'string') {
        finalValues.customerName = finalValues.customerName.trim();
      }

      if (!finalValues.templateId && selectedTemplate) {
        finalValues.templateId = selectedTemplate.id;
      }

      if (finalValues.templateId !== TEMPLATE_IDS.FOUR_SEASONS_SPREAD) {
        finalValues.customerName = '';
      }

      // 调试：打印表单值
      console.log('🔍 表单提交值:', finalValues);
      console.log('🔍 generateNormal:', finalValues.generateNormal);
      console.log('🔍 generateThinking:', finalValues.generateThinking);

      // 验证至少选择一个生成选项
      if (!finalValues.generateNormal && !finalValues.generateThinking) {
        message.error('请至少选择一种分析结果生成方式');
        return;
      }

      // 验证塔罗牌阵图片必须上传（提交时必填），但自动抽牌时可跳过
      if (!finalValues.autoDraw) {
        if (!tarotImageFile && !editingRecord?.tarotImageUrl && !editingRecord?.tarotImagePath) {
          message.error('请上传塔罗牌阵图片后再提交');
          return;
        }
      }

      let recordId: number;
      let llmTriggered = false;

      if (editingRecord) {
        // 如果是草稿记录，更新时标记为非草稿
        const updateData = editingRecord.isDraft ? { ...finalValues, isDraft: false } : finalValues;
        const updateResponse = await DataService.updateRecord(editingRecord.id, updateData);
        recordId = editingRecord.id;
        llmTriggered = updateResponse?.reprocessed === true;
        message.success(editingRecord.isDraft ? '提交成功' : '更新成功');
      } else {
        const response = await DataService.createRecord(finalValues);
        recordId = response.id;
        llmTriggered = true;
        message.success('创建成功');
      }

      // 若期望生成任意一种结果，则标记该记录为“LLM生成中”
      if (llmTriggered && (finalValues.generateNormal || finalValues.generateThinking)) {
        setLlmGenerating(prev => new Set(prev).add(recordId));
      }

      // 如果有塔罗牌阵图片，上传它
      if (tarotImageFile && tarotImageFile.originFileObj) {
        try {
          await handleTarotImageUpload(tarotImageFile.originFileObj, recordId);
        } catch (error) {
          console.error('图片上传失败:', error);
          // 不阻止表单提交，只是提示图片上传失败
        }
      }

      setDrawerVisible(false);
      setEditingRecord(null);
      setShowTemplateSelection(false);
      setSelectedTemplate(null);
      setRelatedOrderData(null); // 清除关联订单数据
      setTarotImageFile(null);
      form.resetFields();
      loadRecords();
    } catch (error: any) {
      // 处理重复订单错误
      if (error.response?.data?.error === 'DUPLICATE_ORDER') {
        message.error(error.response.data.message || '已经存在相同订单号和标题的订单，请检查后重新提交');
      } else {
        message.error(error.response?.data?.message || '操作失败');
      }
    }
  };

  const handleSaveDraft = async (values: any) => {
    try {
      const finalValues = { ...values };

      const normalizedCardCount = parseTarotCardCount(finalValues.cardCount);
      if (normalizedCardCount === null) {
        message.error(`抽牌数量必须是 1-${MAX_TAROT_CARD_COUNT} 之间的整数`);
        return;
      }
      finalValues.cardCount = normalizedCardCount;

      finalValues.interpretationModel = TAROT_INTERPRETATION_MODEL;

      // Ensure switch fields are explicit booleans so later regenerate uses persisted values.
      finalValues.generateNormal = coerceSwitchValue(finalValues.generateNormal, !usesRootDefaults);
      finalValues.generateThinking = coerceSwitchValue(finalValues.generateThinking, true);

      if (typeof finalValues.customerName === 'string') {
        finalValues.customerName = finalValues.customerName.trim();
      }

      if (!finalValues.templateId && selectedTemplate) {
        finalValues.templateId = selectedTemplate.id;
      }

      if (finalValues.templateId !== TEMPLATE_IDS.FOUR_SEASONS_SPREAD) {
        finalValues.customerName = '';
      }

      console.log('🔍 草稿保存值:', finalValues);

      let recordId: number;
      const draftData = { ...finalValues, isDraft: true };

      if (editingRecord) {
        await DataService.updateRecord(editingRecord.id, draftData);
        recordId = editingRecord.id;
        message.success('草稿更新成功');
      } else {
        const response = await DataService.createRecord(draftData);
        recordId = response.id;
        message.success('草稿保存成功');
      }

      // 如果有塔罗牌阵图片，上传它
      if (tarotImageFile && tarotImageFile.originFileObj) {
        try {
          await handleTarotImageUpload(tarotImageFile.originFileObj, recordId);
        } catch (error) {
          console.error('图片上传失败:', error);
        }
      }

      setDrawerVisible(false);
      setEditingRecord(null);
      setShowTemplateSelection(false);
      setSelectedTemplate(null);
      setRelatedOrderData(null); // 清除关联订单数据
      setTarotImageFile(null);
      form.resetFields();
      loadRecords();
    } catch (error: any) {
      // 处理重复订单错误
      if (error.response?.data?.error === 'DUPLICATE_ORDER') {
        message.error(error.response.data.message || '已经存在相同订单号和标题的订单，请检查后重新提交');
      } else {
        message.error(error.response?.data?.message || '草稿保存失败');
      }
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await DataService.deleteRecord(id);
      message.success('删除成功');
      loadRecords();
    } catch (error: any) {
      message.error('删除失败');
    }
  };

  // 切换交付状态
  const handleToggleDeliveryStatus = async (record: DataRecord) => {
    try {
      const newStatus = !record.isDelivered;
      await DataService.updateDeliveryStatus(record.id, newStatus);

      // 更新本地状态
      setRecords(prev => prev.map(r =>
        r.id === record.id ? { ...r, isDelivered: newStatus } : r
      ));

      message.success(newStatus ? '已标记为已交付' : '已标记为未交付');
    } catch (error: any) {
      message.error('更新交付状态失败');
    }
  };

  const handleRegenerateLLM = async (record: DataRecord) => {
    try {
      // 先在本地标记该记录为重新解读中
      setLlmGenerating(prev => new Set(prev).add(record.id));
      await DataService.regenerateLLMResult(record.id);
      message.success('正在重新生成解读结果...');
      // 页面会通过Socket实时更新，无需手动刷新
    } catch (error: any) {
      message.error('重新解读失败');
      // 失败则撤销本地标记
      setLlmGenerating(prev => {
        const next = new Set(prev);
        next.delete(record.id);
        return next;
      });
    }
  };

  async function ensureSessionValid(): Promise<boolean> {
    const token = localStorage.getItem('token');
    if (!token) {
      onSessionExpired?.();
      return false;
    }

    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        onSessionExpired?.();
        return false;
      }
      const payloadBase64Url = parts[1];
      const payloadBase64 = payloadBase64Url.replace(/-/g, '+').replace(/_/g, '/');
      const padded = payloadBase64.padEnd(payloadBase64.length + ((4 - (payloadBase64.length % 4)) % 4), '=');
      const payload = JSON.parse(atob(padded)) as { exp?: number };
      if (typeof payload.exp === 'number') {
        const nowSeconds = Math.floor(Date.now() / 1000);
        if (nowSeconds >= payload.exp - 30) {
          onSessionExpired?.();
          return false;
        }
      }
    } catch {
      onSessionExpired?.();
      return false;
    }

    try {
      await AuthService.getCurrentUser();
      return true;
    } catch (error: any) {
      if (error?.response?.status === 401) {
        if (localStorage.getItem('token')) {
          onSessionExpired?.();
        }
        return false;
      }
      return true;
    }
  }

  const handleCreateRelatedOrder = async (parentRecordId: number, resultType: 'normal' | 'thinking') => {
    const ok = await ensureSessionValid();
    if (!ok) {
      return;
    }

    const parentRecord = records.find(r => r.id === parentRecordId);
    if (!parentRecord) {
      message.error('找不到父订单记录');
      return;
    }

    try {
      const parentData = safeParseRecordData(parentRecord.data);
      
      // 生成关联订单号
      const relatedOrderNumber = `${parentData.orderNumber || 'ORD'}后续单`;
      
      // 设置模板选择状态，跳过录入方式选择
      setShowTemplateSelection(true);
      setShowInputTypeSelection(false);
      setShowImageInput(false);
      setEditingRecord(null);
      setDrawerVisible(true);
      
      // 预填充关联订单信息 - 只包含需要从父订单继承的字段和关联信息
      const relatedData = {
        orderNumber: relatedOrderNumber,
        // 只继承父订单中有实际内容的字段
        ...(parentData.customerInfo && { customerInfo: parentData.customerInfo }),
        ...(parentData.customerDescription && { customerDescription: parentData.customerDescription }),
        // 关联订单的关键信息
        parentRecordId: parentRecordId,
        parentResultType: resultType
      };
      
      // 注意：不再预设 customerQuestion: ''，让模板默认值生效
      
      // 保存到一个临时状态，等模板选择后使用
      setRelatedOrderData(relatedData);
      
      console.log('🔗 准备创建关联订单:', {
        parentId: parentRecordId,
        resultType: resultType,
        relatedOrderNumber: relatedOrderNumber
      });
      
    } catch (error) {
      console.error('创建关联订单失败:', error);
      message.error('解析父订单数据失败');
    }
  };

  const handleNewRecordClick = async () => {
    const ok = await ensureSessionValid();
    if (!ok) {
      return;
    }
    openDrawer();
  };

  const checkFormValid = () => {
    const values = form.getFieldsValue();
    const currentFields = getDataFields(selectedTemplate);
    const useAutoDraw = !!values.autoDraw;
    
    // 验证基础字段
    const isBaseFieldsValid = currentFields.filter(field => field.required).every(field => {
      const value = values[field.name];
      if (field.type === 'number') {
        return parseTarotCardCount(value) !== null;
      }
      return value && value.toString().trim() !== '';
    });
    
    // 验证动态卡牌字段（启用自动抽牌时不强制要求牌面）
    const isCardFieldsValid = useAutoDraw
      ? true
      : Array.from({ length: cardCount }, (_, index) => {
          const cardNameKey = `card_${index}_name`;
          const cardNameValue = values[cardNameKey];
          return cardNameValue && cardNameValue.toString().trim() !== '';
        }).every(isValid => isValid);
    
    // 检查塔罗牌阵图片是否已上传（启用自动抽牌时不需要）
    const hasTarotImage = useAutoDraw
      ? true
      : !!(tarotImageFile || editingRecord?.tarotImageUrl || editingRecord?.tarotImagePath);
    
    setFormValid(isBaseFieldsValid && isCardFieldsValid && hasTarotImage);
  };

  // 监听图片相关状态变化，自动触发表单验证
  useEffect(() => {
    if (drawerVisible && !showTemplateSelection && !showInputTypeSelection && !showImageInput) {
      checkFormValid();
    }
  }, [
    tarotImageFile,
    editingRecord?.tarotImageUrl,
    editingRecord?.tarotImagePath,
    drawerVisible,
    showTemplateSelection,
    showInputTypeSelection,
    showImageInput,
    selectedTemplate,
    form,
    autoDraw,
    cardCount // 当牌数变化时，重新校验可用性
  ]);

  const openDrawer = (record?: DataRecord) => {
    if (record) {
      // 编辑模式：直接打开表单
      setEditingRecord(record);
      const data = safeParseRecordData(record.data);

      // 设置卡牌数量
      const recordCardCount = parseTarotCardCount(data.cardCount) || 3;
      setCardCount(recordCardCount);

      // 确保编辑模式时也有默认值
      const editData = {
        generateNormal: data.generateNormal !== undefined ? data.generateNormal : !usesRootDefaults,
        generateThinking: data.generateThinking !== undefined ? data.generateThinking : true,
        ...data,
        interpretationModel: TAROT_INTERPRETATION_MODEL
      };
      
      console.log('🔍 编辑模式设置的值:', editData);
      console.log('🔍 编辑模式 generateNormal:', editData.generateNormal);
      console.log('🔍 编辑模式 generateThinking:', editData.generateThinking);
      
      form.setFieldsValue(editData);
      setShowTemplateSelection(false);
      setSelectedTemplate(null);
      setDrawerVisible(true);
    } else {
      // 新增模式：先显示模板选择
      setEditingRecord(null);
      setSelectedTemplate(null);
      setCardCount(3);
      form.resetFields();

      // 设置基础默认值
      const basicDefaults = {
        generateNormal: !usesRootDefaults,
        generateThinking: true,
        interpretationModel: TAROT_INTERPRETATION_MODEL,
        cardCount: 3
      };
      form.setFieldsValue(basicDefaults);

      // 强制重新渲染表单
      setFormResetKey(prev => prev + 1);

      setShowTemplateSelection(true);
      setDrawerVisible(true);
    }
  };

  // 获取模板默认值的函数（用于图片识别）
  const getTemplateDefaultValues = (template: Template) => {
    // 使用统一模板系统获取默认值
    const defaultValues = { ...FrontendTemplateAdapter.getTemplateDefaultValues(template) };
    
    // 确保开关字段有默认值
    if (defaultValues.generateNormal === undefined) {
      defaultValues.generateNormal = !usesRootDefaults;
    }
    if (defaultValues.generateThinking === undefined) {
      defaultValues.generateThinking = true;
    }
    defaultValues.interpretationModel = TAROT_INTERPRETATION_MODEL;

    // 为模板添加卡牌信息字段
    const cardInfos = FrontendTemplateAdapter.getCardInfos(template.id);
    if (cardInfos) {
      cardInfos.forEach((info, index) => {
        defaultValues[`card_${index}_info`] = info;
      });
    }

    return {
      ...defaultValues,
      templateId: template.id
    };
  };

  // 设置模板默认值的函数
  const setDefaultValues = (template: Template) => {
    // 先重置表单，清除所有旧数据
    form.resetFields();
    
    // 强制清除所有动态生成的卡牌字段
    const currentValues = form.getFieldsValue();
    Object.keys(currentValues).forEach(key => {
      if (key.startsWith('card_')) {
        form.setFieldValue(key, undefined);
      }
    });
    
    // 使用TemplateManager获取统一的默认值
    const defaultValues = { ...TemplateManager.getDefaultValues(template.id) };

    if (template.id !== TEMPLATE_IDS.FOUR_SEASONS_SPREAD && defaultValues.customerName === undefined) {
      defaultValues.customerName = '';
    }

    if (template.id === TEMPLATE_IDS.FOUR_SEASONS_SPREAD && defaultValues.customerName !== undefined) {
      delete defaultValues.customerName;
    }

    // 确保开关字段有默认值
    if (defaultValues.generateNormal === undefined) {
      defaultValues.generateNormal = !usesRootDefaults;
    }
    if (defaultValues.generateThinking === undefined) {
      defaultValues.generateThinking = true;
    }
    defaultValues.interpretationModel = TAROT_INTERPRETATION_MODEL;
    
    // 使用统一配置获取卡牌数量
    const cardCount = TemplateManager.getCardCount(template.id) || 3;
    setCardCount(cardCount);
    defaultValues.cardCount = cardCount;
    
    defaultValues.templateId = template.id;

    // 设置模板特定的卡牌信息字段
    // 只有对于cardInfos字段被禁用的模板才预填充，否则作为placeholder提示
    const config = TemplateManager.getConfig(template.id);
    const isCardInfosDisabled = TemplateManager.isFieldDisabled(template.id, 'cardInfos');
    if (isCardInfosDisabled) {
      const cardInfos = config?.fieldDefaults?.cardInfos as string[] || [];
      for (let i = 0; i < cardCount; i++) {
        defaultValues[`card_${i}_info`] = cardInfos[i] || '';
      }
    }
    
    console.log('🔧 设置模板默认值:', template.name, defaultValues);
    
    // 应用默认值到表单
    form.setFieldsValue(defaultValues);
    
    // 强制触发表单重新渲染
    setFormResetKey(prev => prev + 1);
    form.validateFields();
  };

  const selectTemplate = (template: Template) => {
    setSelectedTemplate(template);
    setShowTemplateSelection(false);
    
    // 检查是否是关联订单
    if (relatedOrderData) {
      // 关联订单：完全清理表单后再设置数据
      form.resetFields(); // 确保表单完全清空
      
      // 强制清除所有动态生成的卡牌字段
      const currentValues = form.getFieldsValue();
      Object.keys(currentValues).forEach(key => {
        if (key.startsWith('card_')) {
          form.setFieldValue(key, undefined);
        }
      });
      
      const templateConfig = TemplateManager.getConfig(template.id);
      const templateDefaults = templateConfig?.fieldDefaults || {};
      // 直接从模板配置获取卡牌数量，而不是从fieldDefaults
      const templateCardCount = TemplateManager.getCardCount(template.id) || 3;
      
      // 合并数据时，智能合并避免空值覆盖模板默认值
      const finalValues = {
        ...templateDefaults,
        cardCount: templateCardCount, // 使用模板的正确卡牌数量
        templateId: template.id
      };
      
      // 关联订单特殊处理：正确处理card_info字段
      // 只有当模板明确定义了cardInfos字段时才预填充
      const configuredCardInfos = templateConfig?.fieldDefaults?.cardInfos as string[] || [];
      if (configuredCardInfos.length > 0) {
        for (let i = 0; i < templateCardCount; i++) {
          if (configuredCardInfos[i]) {
            (finalValues as any)[`card_${i}_info`] = configuredCardInfos[i];
          }
        }
      }
      // 注意：不为没有配置cardInfos的模板添加任何card_info默认值
      
      // 关联订单的关键字段，必须保留
      const criticalFields = ['parentRecordId', 'parentResultType'];
      
      // 智能复制关联订单字段
      Object.keys(relatedOrderData).forEach(key => {
        const value = relatedOrderData[key];
        // 关键字段始终保留
        if (criticalFields.includes(key)) {
          (finalValues as any)[key] = value;
        }
        // 其他字段只在有意义的值时才覆盖模板默认值
        else if (value !== null && value !== undefined && value !== '') {
          (finalValues as any)[key] = value;
        }
      });
      
      console.log('🔗 关联订单应用模板默认值:', finalValues);
      console.log('🔗 关联订单包含的特殊字段:', { 
        parentRecordId: (finalValues as any).parentRecordId, 
        parentResultType: (finalValues as any).parentResultType 
      });
      form.setFieldsValue(finalValues);
      setCardCount(finalValues.cardCount);
      setFormResetKey(prev => prev + 1);
      form.validateFields();
      
      // 注意：不要在这里清除关联订单临时数据，保留它以便用户可能返回模板选择
      // setRelatedOrderData(null); // 移除这行，改为在表单提交或抽屉关闭时清除
    } else {
      // 正常订单：按原逻辑处理
      if (user.role === 'worker') {
        // 工作者直接使用常规录入
        setDefaultValues(template);
      } else {
        // 管理员显示录入方式选择
        setShowInputTypeSelection(true);
      }
    }
  };


  // 处理录入方式选择
  const handleInputTypeSelection = (type: 'regular' | 'image') => {
    setShowInputTypeSelection(false);
    
    if (type === 'regular') {
      // 常规录入，使用模板特定的默认值
      if (selectedTemplate) {
        setDefaultValues(selectedTemplate);
      }
    } else {
      // 图片录入，显示图片上传界面
      setShowImageInput(true);
    }
  };

  // 处理图片上传和识别
  const handleImageUpload = async (file: File, orderNumber: string, model: string) => {
    setProcessingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('orderNumber', orderNumber);
      formData.append('model', model);

      // 添加选定的模板信息
      if (selectedTemplate) {
        formData.append('templateId', selectedTemplate.id);

        // 获取模板的默认值
        const templateDefaults = getTemplateDefaultValues(selectedTemplate);
        formData.append('templateDefaults', JSON.stringify(templateDefaults));
      }

      const response = await DataService.extractFromImage(formData);

      if (response.success) {
        message.success(response.message);

        // 关闭所有相关的界面状态，返回主界面
        setShowImageInput(false);
        setShowInputTypeSelection(false);
        setShowTemplateSelection(false);
        setDrawerVisible(false);
        setSelectedTemplate(null);
        setEditingRecord(null);
        setCardCount(3);
        setTarotImageFile(null);
        form.resetFields();
        setExtractedData(null);
        setImageFile(null);

        // 刷新记录列表
        loadRecords();
      }
    } catch (error: any) {
      console.error('图片上传失败:', error);
      message.error(error.response?.data?.error || '图片上传失败，请重试');
    } finally {
      setProcessingImage(false);
    }
  };

  // 返回到录入方式选择
  const goBackToInputTypeSelection = () => {
    setShowInputTypeSelection(true);
    setShowImageInput(false);
    setImageFile(null);
    setExtractedData(null);
  };

  const goBackToTemplateSelection = () => {
    setShowTemplateSelection(true);
    setSelectedTemplate(null);
    
    // 先重置表单，彻底清除所有字段
    form.resetFields();
    
    // 重置卡牌数量状态
    setCardCount(3);
    
    // 强制清除所有动态生成的卡牌字段
    const currentValues = form.getFieldsValue();
    Object.keys(currentValues).forEach(key => {
      if (key.startsWith('card_')) {
        form.setFieldValue(key, undefined);
      }
    });
    
    // 重置为基础默认值，不包含任何模板特定信息
    const basicDefaults = {
      generateNormal: !usesRootDefaults,
      generateThinking: true,
      interpretationModel: TAROT_INTERPRETATION_MODEL,
      cardCount: 3,
    };
    
    // 如果是关联订单，需要保留关联订单的基础信息
    if (relatedOrderData) {
      // 合并关联订单的基础字段到默认值中
      Object.assign(basicDefaults, {
        orderNumber: relatedOrderData.orderNumber,
        ...(relatedOrderData.customerInfo && { customerInfo: relatedOrderData.customerInfo }),
        ...(relatedOrderData.customerDescription && { customerDescription: relatedOrderData.customerDescription }),
        parentRecordId: relatedOrderData.parentRecordId,
        parentResultType: relatedOrderData.parentResultType
      });
      console.log('🔗 返回模板选择，保留关联订单基础信息:', basicDefaults);
    }
    
    form.setFieldsValue(basicDefaults);
    
    // 强制重新渲染表单
    setFormResetKey(prev => prev + 1);
    console.log('🔍 返回模板选择，重置为基础默认值:', basicDefaults);
  };

  // 塔罗牌图片上传处理函数
  const handleTarotImageUpload = async (file: any, recordId: number) => {
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('tarotImage', file);

      const response = await DataService.uploadTarotImage(recordId, formData);
      message.success('塔罗牌阵图片上传成功');

      // 不再立即刷新记录列表，而是依赖Socket事件来更新状态
      // 这样可以保持上传状态的准确性，避免覆盖Socket更新的数据
      // loadRecords();

      return response;
    } catch (error) {
      message.error('图片上传失败');
      throw error;
    } finally {
      setUploadingImage(false);
    }
  };

  // 图片预览
  const handleImagePreview = (imageUrl: string) => {
    console.log('🖼️ 预览图片URL:', imageUrl);
    setPreviewImageUrl(imageUrl);
    setImagePreviewVisible(true);
  };

  // 下载生成的图片
  const handleDownloadImage = async (recordId: number) => {
    try {
      const response = await DataService.downloadGeneratedImage(recordId);
      // 创建下载链接
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tarot_result_${recordId}.jpg`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      message.success('图片下载成功');
    } catch (error) {
      message.error('图片下载失败');
    }
  };

  // 前端生成合成图
  const generateCompositeImage = async (record: DataRecord, useThinking: boolean = false): Promise<Blob | null> => {
    const llmResult = useThinking ? record.llmThinkingResult : record.llmResult;

    const tarotImageSource = record.tarotImageUrl || record.tarotImagePath;
    if (!tarotImageSource || !llmResult) {
      message.error(`缺少塔罗牌图片或${useThinking ? '深度思考' : '普通'}分析结果`);
      return null;
    }

    // 生成基于内容的缓存键
    const contentHash = generateContentHash(record, useThinking);
    const compositeKey = `${record.id}_${useThinking ? 'thinking' : 'normal'}_${contentHash}`;
    
    console.log(`🔑 生成图片缓存键: ${compositeKey}`);
    
    setGeneratingComposite(prev => new Set(prev).add(compositeKey));

    try {
      const tarotImageUrl = DataService.getTarotImageUrl(tarotImageSource);
      const data = safeParseRecordData(record.data);
      const title = data.title || '';

      console.log('🖼️ 开始前端合成图片...');
      const blob = await ImageComposer.composeImage({
        tarotImageUrl,
        llmResult: llmResult,
        title: title,
        templateId: typeof data.templateId === 'string' ? data.templateId : '',
        cardCount: typeof data.cardCount === 'number' ? data.cardCount : Number(data.cardCount) || 0,
        titleFontSize: 96, // 可以在这里自定义标题字号
        // 使用默认参数，不覆盖fontSize等设置
        textColor: '#2c3e50',
        backgroundColor: '#ffffff'
      });

      // 保存合成图（使用基于内容哈希的缓存键）
      setCompositeImages(prev => new Map(prev).set(compositeKey, blob));
      
      // 更新记录的当前内容哈希
      setContentHashes(prev => new Map(prev).set(record.id, contentHash));
      
      message.success('合成图生成完成！');
      
      return blob;

    } catch (error) {
      console.error('合成图生成失败:', error);
      message.error('合成图生成失败，请重试');
      return null;
    } finally {
      setGeneratingComposite(prev => {
        const newSet = new Set(prev);
        newSet.delete(compositeKey);
        return newSet;
      });
    }
  };



  // 执行合成图操作
  const executeCompositeAction = async (record: DataRecord, useThinking: boolean = false, action: 'preview' | 'download' | 'copy' = 'preview') => {
    const recordId = record.id;

    // 生成基于内容的缓存键
    const contentHash = generateContentHash(record, useThinking);
    const compositeKey = `${recordId}_${useThinking ? 'thinking' : 'normal'}_${contentHash}`;
    
    console.log(`🔍 查找缓存图片，键: ${compositeKey}`);
    let blob: Blob | null | undefined = compositeImages.get(compositeKey);

    if (!blob) {
      console.log(`📝 缓存中没有找到图片，开始生成...`);
      // 如果还没有生成，先生成并等待返回结果
      blob = await generateCompositeImage(record, useThinking);
      if (!blob) {
        console.error('❌ 图片生成失败，无法执行操作');
        return;
      }
    } else {
      console.log(`✅ 从缓存中找到图片`);
    }

    // 执行相应操作
    switch (action) {
      case 'preview':
        const url = ImageComposer.createPreviewUrl(blob);
        handleImagePreview(url);
        break;
      case 'download':
        const suffix = useThinking ? '_thinking' : '_normal';
        ImageComposer.downloadImage(blob, `tarot_result_${recordId}${suffix}.jpg`);
        message.success('图片下载成功');
        break;
      case 'copy':
        try {
          await ImageComposer.copyToClipboard(blob);
          message.success('图片已复制到剪贴板');
        } catch (error) {
          console.error('复制图片失败:', error);
          message.error('复制图片失败，请重试');
        }
        break;
    }
  };

  // 预览合成图
  const handlePreviewCompositeImage = async (record: DataRecord, useThinking: boolean = false) => {
    try {
      await executeCompositeAction(record, useThinking, 'preview');
    } catch (error) {
      console.error('预览合成图失败:', error);
      message.error('预览失败，请重试');
    }
  };

  // 下载合成图
  const handleDownloadCompositeImage = async (record: DataRecord, useThinking: boolean = false) => {
    try {
      await executeCompositeAction(record, useThinking, 'download');
    } catch (error) {
      console.error('下载合成图失败:', error);
      message.error('下载失败，请重试');
    }
  };

  // 复制合成图到剪贴板
  const handleCopyCompositeImage = async (record: DataRecord, useThinking: boolean = false) => {
    try {
      await executeCompositeAction(record, useThinking, 'copy');
    } catch (error) {
      console.error('复制合成图失败:', error);
      message.error('复制失败，请重试');
    }
  };

  const generateCompositePdf = async (record: DataRecord, useThinking: boolean = false): Promise<Blob | null> => {
    const llmResult = useThinking ? record.llmThinkingResult : record.llmResult;
    const tarotImageSource = record.tarotImageUrl || record.tarotImagePath;

    if (!tarotImageSource || !llmResult) {
      message.error(`缺少塔罗牌图片或${useThinking ? '深度思考' : '普通'}分析结果`);
      return null;
    }

    const contentHash = generateContentHash(record, useThinking);
    const pdfKey = `${record.id}_${useThinking ? 'thinking' : 'normal'}_${contentHash}_pdf`;

    setGeneratingPdf(prev => new Set(prev).add(pdfKey));

    try {
      const tarotImageUrl = DataService.getTarotImageUrl(tarotImageSource);
      const data = safeParseRecordData(record.data);
      const title = data.title || '';

      const pdfBlob = await PdfComposer.composePdf({
        tarotImageUrl,
        llmResult,
        title,
        templateId: typeof data.templateId === 'string' ? data.templateId : ''
      });

      setCompositePdfs(prev => new Map(prev).set(pdfKey, pdfBlob));
      message.success('PDF生成完成！');
      return pdfBlob;
    } catch (error) {
      console.error('PDF生成失败:', error);
      return null;
    } finally {
      setGeneratingPdf(prev => {
        const next = new Set(prev);
        next.delete(pdfKey);
        return next;
      });
    }
  };

  const executePdfAction = async (record: DataRecord, useThinking: boolean = false, action: 'preview' | 'download' = 'preview') => {
    const recordId = record.id;
    const contentHash = generateContentHash(record, useThinking);
    const pdfKey = `${recordId}_${useThinking ? 'thinking' : 'normal'}_${contentHash}_pdf`;

    let blob: Blob | null | undefined = compositePdfs.get(pdfKey);

    if (!blob) {
      blob = await generateCompositePdf(record, useThinking);
      if (!blob) {
        return;
      }
    }

    switch (action) {
      case 'preview': {
        const url = PdfComposer.createPreviewUrl(blob);
        window.open(url, '_blank');
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
        break;
      }
      case 'download': {
        const recordData = safeParseRecordData(record.data);
        const pdfFileName = buildPdfFileName(record, recordData);
        PdfComposer.downloadPdf(blob, pdfFileName);
        message.success('PDF下载成功');
        break;
      }
    }
  };

  const handlePreviewCompositePdf = async (record: DataRecord, useThinking: boolean = false) => {
    try {
      await executePdfAction(record, useThinking, 'preview');
    } catch (error) {
      console.error('预览PDF失败:', error);
      message.error('预览PDF失败，请重试');
    }
  };

  const handleDownloadCompositePdf = async (record: DataRecord, useThinking: boolean = false) => {
    try {
      await executePdfAction(record, useThinking, 'download');
    } catch (error) {
      console.error('下载PDF失败:', error);
      message.error('下载PDF失败，请重试');
    }
  };

  // 打开编辑AI分析结果抽屉
  const openEditLLMDrawer = (record: DataRecord) => {
    setEditingLLMRecord(record);
    setEditingThinkingResult(false);
    llmEditForm.setFieldsValue({
      llmResult: formatFullWidthPunctuation(record.llmResult)
    });
    setEditLLMDrawerVisible(true);
  };

  // 打开编辑深度思考分析结果抽屉
  const openEditThinkingLLMDrawer = (record: DataRecord) => {
    setEditingLLMRecord(record);
    setEditingThinkingResult(true);
    llmEditForm.setFieldsValue({
      llmResult: formatFullWidthPunctuation(record.llmThinkingResult)
    });
    setEditLLMDrawerVisible(true);
  };

  // 保存AI分析结果
  const handleSaveLLMResult = async (values: { llmResult: string }) => {
    if (!editingLLMRecord) return;

    try {
      if (editingThinkingResult) {
        // 更新深度思考结果
        await DataService.updateLLMThinkingResult(editingLLMRecord.id, values.llmResult);

        // 更新本地状态
        setRecords(prev => prev.map(record =>
          record.id === editingLLMRecord.id
            ? { ...record, llmThinkingResult: values.llmResult }
            : record
        ));

        // 清除该记录的深度思考合成图缓存
        setCompositeImages(prev => {
          const newMap = new Map(prev);
          // 删除所有与该记录相关的深度思考缓存条目
          for (const key of newMap.keys()) {
            if (key.startsWith(`${editingLLMRecord.id}_thinking_`)) {
              newMap.delete(key);
            }
          }
          return newMap;
        });

        setCompositePdfs(prev => {
          const newMap = new Map(prev);
          for (const key of newMap.keys()) {
            if (key.startsWith(`${editingLLMRecord.id}_thinking_`)) {
              newMap.delete(key);
            }
          }
          return newMap;
        });

        message.success('深度思考分析结果更新成功');
      } else {
        // 更新普通结果
        await DataService.updateLLMResult(editingLLMRecord.id, values.llmResult);

        // 更新本地状态
        setRecords(prev => prev.map(record =>
          record.id === editingLLMRecord.id
            ? { ...record, llmResult: values.llmResult }
            : record
        ));

        // 清除该记录的普通合成图缓存
        setCompositeImages(prev => {
          const newMap = new Map(prev);
          // 删除所有与该记录相关的普通缓存条目
          for (const key of newMap.keys()) {
            if (key.startsWith(`${editingLLMRecord.id}_normal_`)) {
              newMap.delete(key);
            }
          }
          return newMap;
        });

        setCompositePdfs(prev => {
          const newMap = new Map(prev);
          for (const key of newMap.keys()) {
            if (key.startsWith(`${editingLLMRecord.id}_normal_`)) {
              newMap.delete(key);
            }
          }
          return newMap;
        });

        message.success('AI分析结果更新成功');
      }

      setEditLLMDrawerVisible(false);
      setEditingLLMRecord(null);
      setEditingThinkingResult(false);
      llmEditForm.resetFields();
    } catch (error: any) {
      console.error('更新AI分析结果失败:', error);
      message.error('更新失败，请重试');
    }
  };

  // 复制订单号到剪贴板
  const handleCopyOrderNumber = async (orderNumber: string) => {
    try {
      if (!navigator.clipboard) {
        message.error('浏览器不支持剪贴板功能，请升级浏览器或使用HTTPS访问');
        return;
      }

      await navigator.clipboard.writeText(orderNumber);
      message.success('订单号已复制到剪贴板');
    } catch (error: any) {
      console.error('复制订单号失败:', error);
      message.error('复制失败，请手动复制');
    }
  };

  // 处理统计功能
  const handleStatistics = async (values: { startDateTime: string; endDateTime: string }) => {
    if (!values.startDateTime || !values.endDateTime) {
      message.error('请选择起止日期时间');
      return;
    }

    setStatisticsLoading(true);
    try {
      // 转换为API需要的格式
      const startDateTimeAPI = formatDateTimeForAPI(values.startDateTime);
      const endDateTimeAPI = formatDateTimeForAPI(values.endDateTime);

      const statistics: StatisticsResult = await DataService.getStatistics(startDateTimeAPI, endDateTimeAPI);

      // 构建详细的统计结果，显示用户输入的UTC+8时间
      const startDisplayTime = values.startDateTime.replace('T', ' ');
      const endDisplayTime = values.endDateTime.replace('T', ' ');
      let result = `${startDisplayTime} 至 ${endDisplayTime} 期间（UTC+8），共完成 ${statistics.orderCount} 单，总计 ${statistics.totalCards} 张牌，累计 ${statistics.totalCardTimes} 牌次。`;

      // 添加按牌数分组的统计信息
      if (statistics.cardCountGroups && statistics.cardCountGroups.length > 0) {
        const groupDetails = statistics.cardCountGroups
          .map((group: { cardCount: number; orderCount: number }) => `${group.cardCount} 张牌的 ${group.orderCount} 单`)
          .join('，');
        result += `其中包括 ${groupDetails}。`;
      }

      setStatisticsResult(result);
      message.success('统计完成');
    } catch (error: any) {
      console.error('统计失败:', error);
      message.error('统计失败，请重试');
    } finally {
      setStatisticsLoading(false);
    }
  };

  // 打开统计模态框时设置默认日期
  const openStatisticsModal = () => {
    const [startDateStr, endDateStr] = getDefaultDateRange();

    // 设置表单默认值
    statisticsForm.setFieldsValue({
      startDateTime: startDateStr,
      endDateTime: endDateStr
    });

    setStatisticsModalVisible(true);
  };

  // 复制统计结果到剪贴板
  const handleCopyStatisticsResult = async () => {
    try {
      if (!navigator.clipboard) {
        message.error('浏览器不支持剪贴板功能，请升级浏览器或使用HTTPS访问');
        return;
      }

      await navigator.clipboard.writeText(statisticsResult);
      message.success('统计结果已复制到剪贴板');
    } catch (error: any) {
      console.error('复制统计结果失败:', error);
      message.error('复制失败，请手动复制');
    }
  };

  // CSV导出功能
  const handleExportCSV = async () => {
    try {
      const values = statisticsForm.getFieldsValue();
      if (!values.startDateTime || !values.endDateTime) {
        message.error('请先设置时间范围');
        return;
      }

      setCsvExportLoading(true);
      
      // 转换为API需要的格式
      const startDateTimeAPI = formatDateTimeForAPI(values.startDateTime);
      const endDateTimeAPI = formatDateTimeForAPI(values.endDateTime);

      const response = await DataService.exportStatisticsCSV(startDateTimeAPI, endDateTimeAPI);
      
      // 创建下载链接
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      // 从响应头获取文件名，如果没有则使用默认名称
      const contentDisposition = response.headers['content-disposition'];
      let filename = `用户统计_${values.startDateTime.split('T')[0]}_到_${values.endDateTime.split('T')[0]}.csv`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((["\'])(.*?)\2|([^;\n]*))/);
        if (filenameMatch && filenameMatch[3]) {
          filename = filenameMatch[3];
        }
      }
      
      // 创建下载链接
      const url = window.URL.createObjectURL(blob);
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      message.success('CSV文件导出成功');
    } catch (error: any) {
      console.error('CSV导出失败:', error);
      if (error.response?.status === 403) {
        message.error('权限不足，仅管理员和控制者可以导出CSV');
      } else {
        message.error('CSV导出失败，请重试');
      }
    } finally {
      setCsvExportLoading(false);
    }
  };

  // 复制图片到剪贴板（改进版，用于后端生成的图片）
  const handleCopyImage = async (imageUrl: string) => {
    try {
      console.log('🔗 开始复制图片:', imageUrl);

      // 检查浏览器支持
      if (!navigator.clipboard || !window.ClipboardItem) {
        message.error('浏览器不支持剪贴板功能，请升级浏览器或使用HTTPS访问');
        return;
      }

      // 显示加载提示
      const loadingMessage = message.loading('正在复制图片...', 0);

      try {
        // 使用更兼容的fetch方式获取图片
        const response = await fetch(imageUrl, {
          method: 'GET',
          headers: {
            'Accept': 'image/*',
            'Cache-Control': 'no-cache',
          },
          mode: 'cors',
          credentials: 'same-origin', // 添加凭证
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        let blob = await response.blob();
        console.log('📷 图片获取成功，类型:', blob.type, '大小:', blob.size);

        // 如果图片类型不明确，尝试从URL推断
        if (!blob.type || blob.type === 'application/octet-stream') {
          const extension = imageUrl.split('.').pop()?.toLowerCase();
          let mimeType = 'image/png'; // 默认

          switch (extension) {
            case 'jpg':
            case 'jpeg':
              mimeType = 'image/jpeg';
              break;
            case 'png':
              mimeType = 'image/png';
              break;
            case 'gif':
              mimeType = 'image/gif';
              break;
            case 'webp':
              mimeType = 'image/webp';
              break;
          }

          console.log('修正图片类型从', blob.type, '到', mimeType);
          blob = new Blob([blob], { type: mimeType });
        }

        // 使用ImageComposer的改进复制方法
        await ImageComposer.copyToClipboard(blob);

        loadingMessage();
        message.success('图片已复制到剪贴板');
        console.log('✅ 图片复制成功');

      } catch (clipboardError: any) {
        loadingMessage();
        console.error('剪贴板操作失败:', clipboardError);

        // 提供备选方案
        message.error(`复制失败: ${clipboardError.message}`);

        // 尝试下载作为备选方案
        try {
          const link = document.createElement('a');
          link.href = imageUrl;
          link.target = '_blank';
          link.download = `tarot_result_${Date.now()}.jpg`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          message.info('复制失败，已触发下载，请保存图片后手动复制');
        } catch (downloadError) {
          console.error('下载也失败了:', downloadError);
          message.error('复制和下载都失败，请在新窗口中右键保存图片');

          // 最后的备选方案：在新窗口打开
          window.open(imageUrl, '_blank');
        }
      }

    } catch (error: any) {
      console.error('复制图片失败:', error);
      message.error(`复制失败: ${error.message}`);
    }
  };

  // 数据记录页面内容
  const renderDataRecords = () => (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>{(user.role === 'admin' || user.role === 'controller') ? '所有数据记录' : '我的数据记录'}</h2>
        <Space>
          <Button
            icon={<BarChartOutlined />}
            onClick={openStatisticsModal}
          >
            统计
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => void handleNewRecordClick()}
          >
            新增记录
          </Button>
        </Space>
      </div>

      {/* 搜索框 */}
      <div style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="输入订单号进行搜索"
          value={searchValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearch(e.target.value)}
          onSearch={handleSearchEnter}
          style={{ maxWidth: 400 }}
          allowClear
        />
      </div>

      <List
        loading={loading}
        dataSource={records}
        renderItem={(record: DataRecord) => {
          const data = safeParseRecordData(record.data);

          // 分离卡牌数据和基础数据
          const cardData: { [key: string]: any } = {};
          const baseData: { [key: string]: any } = {};

          Object.entries(data).forEach(([key, value]: [string, any]) => {
            if (key.startsWith('card_')) {
              cardData[key] = value;
            } else {
              baseData[key] = value;
            }
          });

          // 组织卡牌数据
          const cards: Array<{ name: string; info: string }> = [];
          const cardCount = baseData.cardCount || 0;
          for (let i = 0; i < cardCount; i++) {
            cards.push({
              name: cardData[`card_${i}_name`] || '',
              info: cardData[`card_${i}_info`] || ''
            });
          }

          const isExpanded = expandedRecords.has(record.id);

          // 期望生成的类型：旧数据未显式设置时默认为 true
          const expectedNormal = baseData.generateNormal !== false;
          const expectedThinking = baseData.generateThinking !== false;
          const missingNormal = expectedNormal && !record.llmResult;
          const missingThinking = expectedThinking && !record.llmThinkingResult;
          const isRegenerating = llmGenerating.has(record.id);
          const isLLMWaiting = missingNormal || missingThinking || isRegenerating;

          return (
            <List.Item
              ref={(el: HTMLDivElement | null) => recordRefs.current[record.id] = el}
              style={{ transition: 'background-color 0.3s', padding: 0 }}
            >
              <div style={{ width: '100%' }}>
                {!isExpanded ? (
                  // 折叠状态：显示在圆角矩形框内
                  <div
                    style={{
                      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                      borderRadius: '12px',
                      padding: '16px',
                      margin: '8px 0',
                      border: '1px solid #e8e8e8',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    }}
                    onClick={() => toggleExpanded(record.id)}
                    onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                    }}
                  >
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                        <DownOutlined style={{ color: '#1890ff', marginRight: '8px', fontSize: '12px' }} />
                        <span style={{ fontSize: '16px', fontWeight: '600', color: '#262626' }}>
                          订单号: {baseData.orderNumber || '未命名'}
                        </span>
                        <Button
                          type="text"
                          size="small"
                          icon={<CopyOutlined />}
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            handleCopyOrderNumber(baseData.orderNumber || '');
                          }}
                          style={{
                            marginLeft: '8px',
                            color: '#1890ff',
                            padding: '0 4px',
                            height: '24px',
                            minWidth: '24px'
                          }}
                          title="复制订单号"
                        />
                        <Tag style={{ marginLeft: '8px', height: '24px', lineHeight: '22px' }} color="blue">ID: {record.id}</Tag>
                        {isLLMWaiting && (
                          <span style={{ marginLeft: 8 }}>
                            <Badge status="processing" text="解读中" />
                          </span>
                        )}
                        {/* Root用户的删除按钮 */}
                        {user.username === 'root' && (
                          <Popconfirm
                            title="确定要删除这条记录吗？"
                            onConfirm={(e?: React.MouseEvent) => {
                              if (e) e.stopPropagation();
                              handleDelete(record.id);
                            }}
                            okText="确定"
                            cancelText="取消"
                          >
                            <Button
                              icon={<DeleteOutlined />}
                              danger
                              size="small"
                              style={{
                                marginLeft: '8px',
                                height: '24px',
                                padding: '0 8px'
                              }}
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                              }}
                            >
                              删除
                            </Button>
                          </Popconfirm>
                        )}
                        {/* 显示处理状态 */}
                        {data._isProcessing && (
                          <Tag color="orange" style={{ marginLeft: '8px', height: '24px', lineHeight: '22px' }}>
                            {data._processingStatus === 'extracting' ? '图片识别中...' : '处理中...'}
                          </Tag>
                        )}
                        {data._processingStatus === 'failed' && (
                          <Tag color="red" style={{ marginLeft: '8px', height: '24px', lineHeight: '22px' }}>识别失败</Tag>
                        )}
                        {record.isDraft && !data._isProcessing && (
                          <Tag color="orange" style={{ marginLeft: '8px', height: '24px', lineHeight: '22px' }}>草稿</Tag>
                        )}
                      </div>
                      {baseData.title && (
                        <div style={{ marginBottom: '8px' }}>
                          <span style={{ fontSize: '14px', fontWeight: '500', color: '#1890ff' }}>
                            标题: {baseData.title}
                          </span>
                          {record.llmCallCount !== undefined && (
                            <span style={{ fontSize: '14px', fontWeight: '500', color: '#eb2f96', marginLeft: '12px' }}>
                              解读次数: {record.llmCallCount}
                            </span>
                          )}
                        </div>
                      )}
                      <div style={{ marginBottom: '8px' }}>
                        <Tag color="green" style={{ fontSize: '12px', height: '24px', lineHeight: '22px' }}>
                          录入时间: {formatDateToUTC8(record.createdAt)}
                        </Tag>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                        <Tag color="green" style={{ fontSize: '12px', height: '24px', lineHeight: '22px' }}>
                          组织: {record.organizationName || '未知'}
                        </Tag>
                        <Tag color="green" style={{ fontSize: '12px', height: '24px', lineHeight: '22px' }}>
                          用户: {record.username || '未知'}
                        </Tag>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                        <Button
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            handleToggleDeliveryStatus(record);
                          }}
                          size="small"
                          type={record.isDelivered ? "primary" : "default"}
                          style={{
                            backgroundColor: record.isDelivered ? '#52c41a' : '#f5f5f5',
                            borderColor: record.isDelivered ? '#52c41a' : '#d9d9d9',
                            color: record.isDelivered ? '#ffffff' : '#595959',
                            fontWeight: '500',
                            borderRadius: '6px',
                            transition: 'all 0.3s ease',
                            boxShadow: record.isDelivered ? '0 2px 4px rgba(82, 196, 26, 0.3)' : 'none',
                            height: '24px'
                          }}
                          onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                            if (record.isDelivered) {
                              e.currentTarget.style.backgroundColor = '#73d13d';
                              e.currentTarget.style.borderColor = '#73d13d';
                            } else {
                              e.currentTarget.style.backgroundColor = '#e6f7ff';
                              e.currentTarget.style.borderColor = '#91d5ff';
                              e.currentTarget.style.color = '#1890ff';
                            }
                          }}
                          onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                            if (record.isDelivered) {
                              e.currentTarget.style.backgroundColor = '#52c41a';
                              e.currentTarget.style.borderColor = '#52c41a';
                            } else {
                              e.currentTarget.style.backgroundColor = '#f5f5f5';
                              e.currentTarget.style.borderColor = '#d9d9d9';
                              e.currentTarget.style.color = '#595959';
                            }
                          }}
                        >
                          {record.isDelivered ? '✓ 已交付' : '○ 未交付'}
                        </Button>
                        <Button
                          icon={<EditOutlined />}
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            openDrawer(record);
                          }}
                          size="small"
                          type="primary"
                          ghost
                          style={{
                            height: '24px'
                          }}
                        >
                          编辑
                        </Button>
                        {/* 重新解读按钮 */}
                        {(record.llmResult || record.llmThinkingResult) && (
                          <Button
                            icon={<EditOutlined />}
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              handleRegenerateLLM(record);
                            }}
                            size="small"
                            style={{
                              backgroundColor: 'transparent',
                              borderColor: '#ff7f00',
                              color: '#ff7f00',
                              borderRadius: '6px',
                              fontWeight: '500',
                              height: '24px'
                            }}
                            ghost
                          >
                            重新解读
                          </Button>
                        )}
                        {/* 删除功能已禁用 */}
                        {/* <Popconfirm
                          title="确定要删除这条记录吗？"
                          onConfirm={() => handleDelete(record.id)}
                          okText="确定"
                          cancelText="取消"
                        >
                          <Button
                            icon={<DeleteOutlined />}
                            danger
                            size="small"
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                          >
                            删除
                          </Button>
                        </Popconfirm> */}
                      </div>
                      <div style={{ color: '#8c8c8c', fontSize: '13px' }}>
                        点击展开查看详细信息...
                      </div>
                    </div>
                  </div>
                ) : (
                  // 展开状态：显示在圆角矩形框内
                  <div
                    style={{
                      background: '#ffffff',
                      borderRadius: '12px',
                      padding: '20px',
                      margin: '8px 0',
                      border: '1px solid #d9d9d9',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    }}
                  >
                    {/* 标题区域 */}
                    <div style={{ marginBottom: '16px', borderBottom: '1px solid #f0f0f0', paddingBottom: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                        <Button
                          type="text"
                          size="small"
                          icon={<UpOutlined />}
                          onClick={() => toggleExpanded(record.id)}
                          style={{ marginRight: '8px', color: '#1890ff' }}
                        />
                        <span style={{ fontSize: '18px', fontWeight: '600', color: '#262626' }}>
                          订单号: {baseData.orderNumber || '未命名'}
                        </span>
                        <Button
                          type="text"
                          size="small"
                          icon={<CopyOutlined />}
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            handleCopyOrderNumber(baseData.orderNumber || '');
                          }}
                          style={{
                            marginLeft: '8px',
                            color: '#1890ff',
                            padding: '0 4px',
                            height: '24px',
                            minWidth: '24px'
                          }}
                          title="复制订单号"
                        />
                        <Tag style={{ marginLeft: '8px', height: '24px', lineHeight: '22px' }} color="blue">ID: {record.id}</Tag>
                        {isLLMWaiting && (
                          <span style={{ marginLeft: 8 }}>
                            <Badge status="processing" text="解读中" />
                          </span>
                        )}
                        {/* Root用户的删除按钮 */}
                        {user.username === 'root' && (
                          <Popconfirm
                            title="确定要删除这条记录吗？"
                            onConfirm={(e?: React.MouseEvent) => {
                              if (e) e.stopPropagation();
                              handleDelete(record.id);
                            }}
                            okText="确定"
                            cancelText="取消"
                          >
                            <Button
                              icon={<DeleteOutlined />}
                              danger
                              size="small"
                              style={{
                                marginLeft: '8px',
                                height: '24px',
                                padding: '0 8px'
                              }}
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                              }}
                            >
                              删除
                            </Button>
                          </Popconfirm>
                        )}
                        {/* 显示处理状态 */}
                        {data._isProcessing && (
                          <Tag color="orange" style={{ marginLeft: '8px', height: '24px', lineHeight: '22px' }}>
                            {data._processingStatus === 'extracting' ? '图片识别中...' : '处理中...'}
                          </Tag>
                        )}
                        {data._processingStatus === 'failed' && (
                          <Tag color="red" style={{ marginLeft: '8px', height: '24px', lineHeight: '22px' }}>识别失败</Tag>
                        )}
                        {record.isDraft && !data._isProcessing && (
                          <Tag color="orange" style={{ marginLeft: '8px', height: '24px', lineHeight: '22px' }}>草稿</Tag>
                        )}
                      </div>
                      {baseData.title && (
                        <div style={{ marginBottom: '12px' }}>
                          <span style={{ fontSize: '16px', fontWeight: '500', color: '#1890ff' }}>
                            标题: {baseData.title}
                          </span>
                          {record.llmCallCount !== undefined && (
                            <span style={{ fontSize: '16px', fontWeight: '500', color: '#eb2f96', marginLeft: '12px' }}>
                              解读次数: {record.llmCallCount}
                            </span>
                          )}
                        </div>
                      )}
                      <div style={{ marginBottom: '12px' }}>
                        <Tag color="green" style={{ fontSize: '13px', height: '24px', lineHeight: '22px' }}>
                          录入时间: {formatDateToUTC8(record.createdAt)}
                        </Tag>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                        <Tag color="green" style={{ fontSize: '13px', height: '24px', lineHeight: '22px' }}>
                          组织: {record.organizationName || '未知'}
                        </Tag>
                        <Tag color="green" style={{ fontSize: '13px', height: '24px', lineHeight: '22px' }}>
                          用户: {record.username || '未知'}
                        </Tag>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <Button
                          onClick={() => handleToggleDeliveryStatus(record)}
                          size="small"
                          type={record.isDelivered ? "primary" : "default"}
                          style={{
                            backgroundColor: record.isDelivered ? '#52c41a' : '#f5f5f5',
                            borderColor: record.isDelivered ? '#52c41a' : '#d9d9d9',
                            color: record.isDelivered ? '#ffffff' : '#595959',
                            fontWeight: '500',
                            borderRadius: '6px',
                            transition: 'all 0.3s ease',
                            boxShadow: record.isDelivered ? '0 2px 4px rgba(82, 196, 26, 0.3)' : 'none',
                            height: '24px'
                          }}
                          onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                            if (record.isDelivered) {
                              e.currentTarget.style.backgroundColor = '#73d13d';
                              e.currentTarget.style.borderColor = '#73d13d';
                            } else {
                              e.currentTarget.style.backgroundColor = '#e6f7ff';
                              e.currentTarget.style.borderColor = '#91d5ff';
                              e.currentTarget.style.color = '#1890ff';
                            }
                          }}
                          onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                            if (record.isDelivered) {
                              e.currentTarget.style.backgroundColor = '#52c41a';
                              e.currentTarget.style.borderColor = '#52c41a';
                            } else {
                              e.currentTarget.style.backgroundColor = '#f5f5f5';
                              e.currentTarget.style.borderColor = '#d9d9d9';
                              e.currentTarget.style.color = '#595959';
                            }
                          }}
                        >
                          {record.isDelivered ? '✓ 已交付' : '○ 未交付'}
                        </Button>
                        <Button
                          icon={<EditOutlined />}
                          onClick={() => openDrawer(record)}
                          size="small"
                          type="primary"
                          ghost
                          style={{
                            height: '24px'
                          }}
                        >
                          编辑
                        </Button>
                        {/* 重新解读按钮 */}
                        {(record.llmResult || record.llmThinkingResult) && (
                          <Button
                            icon={<EditOutlined />}
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              handleRegenerateLLM(record);
                            }}
                            size="small"
                            style={{
                              backgroundColor: 'transparent',
                              borderColor: '#ff7f00',
                              color: '#ff7f00',
                              borderRadius: '6px',
                              fontWeight: '500',
                              height: '24px'
                            }}
                            ghost
                          >
                            重新解读
                          </Button>
                        )}
                        {/* 删除功能已禁用 */}
                        {/* <Popconfirm
                          title="确定要删除这条记录吗？"
                          onConfirm={() => handleDelete(record.id)}
                          okText="确定"
                          cancelText="取消"
                        >
                          <Button
                            icon={<DeleteOutlined />}
                            danger
                            size="small"
                          >
                            删除
                          </Button>
                        </Popconfirm> */}
                      </div>
                    </div>

                    {/* 详细内容区域 */}
                    <div style={{ display: 'grid', gap: '16px' }}>
                      <div style={{ background: '#fafafa', padding: '12px', borderRadius: '8px' }}>
                        <div style={{ fontWeight: '600', color: '#262626', marginBottom: '8px' }}>客户信息</div>
                        <div style={{ color: '#595959', lineHeight: '1.6' }}>{baseData.customerInfo || '无'}</div>
                      </div>

                      <div style={{ background: '#fafafa', padding: '12px', borderRadius: '8px' }}>
                        <div style={{ fontWeight: '600', color: '#262626', marginBottom: '8px' }}>客户自述</div>
                        <div style={{ color: '#595959', lineHeight: '1.6' }}>{baseData.customerDescription || '无'}</div>
                      </div>

                      <div style={{ background: '#fafafa', padding: '12px', borderRadius: '8px' }}>
                        <div style={{ fontWeight: '600', color: '#262626', marginBottom: '8px' }}>问题</div>
                        <div style={{ color: '#595959', lineHeight: '1.6' }}>{baseData.customerQuestion || '无'}</div>
                      </div>

                      <div style={{ background: '#fafafa', padding: '12px', borderRadius: '8px' }}>
                        <div style={{ fontWeight: '600', color: '#262626', marginBottom: '8px' }}>抽牌数量</div>
                        <div style={{ color: '#595959' }}>{baseData.cardCount || 0}</div>
                      </div>

                      {cards.length > 0 && (
                        <div style={{ background: '#fafafa', padding: '12px', borderRadius: '8px' }}>
                          <div style={{ fontWeight: '600', color: '#262626', marginBottom: '12px' }}>卡牌信息</div>
                          <div style={{ display: 'grid', gap: '8px' }}>
                            {cards.map((card, index) => (
                              <div key={index} style={{
                                background: '#ffffff',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                border: '1px solid #e8e8e8'
                              }}>
                                <div style={{ fontWeight: '500', color: '#262626' }}>第{index + 1}张: {card.name}</div>
                                {card.info && (
                                  <div style={{ color: '#8c8c8c', fontSize: '12px', marginTop: '4px' }}>
                                    {card.info}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {(record.tarotImageUrl || record.tarotImagePath) && (
                        <div style={{ background: '#fafafa', padding: '12px', borderRadius: '8px' }}>
                          <div style={{ fontWeight: '600', color: '#262626', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            塔罗牌阵
                            {record.tarotImageUploadStatus === 'uploading' && (
                              <span style={{ fontSize: '12px', color: '#1890ff', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <LoadingOutlined />
                                上传中...
                              </span>
                            )}
                            {record.tarotImageUploadStatus === 'failed' && (
                              <span style={{ fontSize: '12px', color: '#ff4d4f' }}>
                                上传失败
                              </span>
                            )}
                            {record.tarotImageUploadStatus === 'completed' && record.tarotImageUrl && (
                              <span style={{ fontSize: '12px', color: '#52c41a' }}>
                                已上传云端
                              </span>
                            )}
                          </div>
                          <Button
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => handleImagePreview(DataService.getTarotImageUrl(record.tarotImageUrl || record.tarotImagePath!))}
                            type="primary"
                            ghost
                          >
                            预览图片
                          </Button>
                        </div>
                      )}

                      {(record.llmResult || record.llmThinkingResult) && (
                        <div style={{ background: '#f0f9ff', padding: '16px', borderRadius: '8px', border: '1px solid #bae7ff' }}>
                          <div style={{ fontWeight: '600', color: '#262626', marginBottom: '12px' }}>AI分析结果</div>

                          {record.llmResult && (
                            <div style={{ marginBottom: '16px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                <div style={{ fontWeight: '500', color: '#1890ff' }}>普通分析结果</div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <Button
                                    icon={<LinkOutlined />}
                                    onClick={(e: React.MouseEvent) => {
                                      e.stopPropagation();
                                      void handleCreateRelatedOrder(record.id, 'normal');
                                    }}
                                    size="small"
                                    style={{
                                      backgroundColor: 'transparent',
                                      borderColor: '#FF894F',
                                      color: '#FF894F',
                                      borderRadius: '6px',
                                      fontWeight: '500',
                                      height: '24px'
                                    }}
                                    ghost
                                    title="基于普通分析结果创建关联订单"
                                  >
                                    关联订单
                                  </Button>
                                  <Button
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={() => openEditLLMDrawer(record)}
                                    title="编辑AI分析结果"
                                    type="primary"
                                    ghost
                                  >
                                    编辑
                                  </Button>
                                </div>
                              </div>
                              <div style={{
                                whiteSpace: 'pre-wrap',
                                background: '#ffffff',
                                padding: '12px',
                                borderRadius: '6px',
                                border: '1px solid #e8e8e8',
                                lineHeight: '1.6',
                                color: '#262626'
                              }}>
                                {formatFullWidthPunctuation(record.llmResult)}
                              </div>
                              {(record.tarotImageUrl || record.tarotImagePath) && (
                                <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                  <Button
                                    size="small"
                                    icon={<EyeOutlined />}
                                    onClick={() => handlePreviewCompositeImage(record, false)}
                                    loading={isGeneratingCompositeImage(record, false, generatingComposite)}
                                    type="primary"
                                    ghost
                                  >
                                    {isGeneratingCompositeImage(record, false, generatingComposite) ? '生成中...' : '预览合成图'}
                                  </Button>
                                  <Button
                                    size="small"
                                    icon={<DownloadOutlined />}
                                    onClick={() => handleDownloadCompositeImage(record, false)}
                                    loading={isGeneratingCompositeImage(record, false, generatingComposite)}
                                  >
                                    下载
                                  </Button>
                                  <Button
                                    size="small"
                                    icon={<CopyOutlined />}
                                    onClick={() => handleCopyCompositeImage(record, false)}
                                    title="复制图片到剪贴板"
                                    loading={isGeneratingCompositeImage(record, false, generatingComposite)}
                                  >
                                    复制
                                  </Button>
                                  <Button
                                    size="small"
                                    icon={<FilePdfOutlined />}
                                    onClick={() => handlePreviewCompositePdf(record, false)}
                                    loading={isGeneratingPdfDocument(record, false, generatingPdf)}
                                    type="primary"
                                    ghost
                                  >
                                    {isGeneratingPdfDocument(record, false, generatingPdf) ? '生成PDF...' : '预览PDF'}
                                  </Button>
                                  <Button
                                    size="small"
                                    icon={<FilePdfOutlined />}
                                    onClick={() => handleDownloadCompositePdf(record, false)}
                                    loading={isGeneratingPdfDocument(record, false, generatingPdf)}
                                  >
                                    下载PDF
                                  </Button>
                                </div>
                              )}
                            </div>
                          )}

                          {record.llmThinkingResult && (
                            <div style={{ marginBottom: '8px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                <div style={{ fontWeight: '500', color: '#722ed1' }}>深度思考分析结果</div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <Button
                                    icon={<LinkOutlined />}
                                    onClick={(e: React.MouseEvent) => {
                                      e.stopPropagation();
                                      void handleCreateRelatedOrder(record.id, 'thinking');
                                    }}
                                    size="small"
                                    style={{
                                      backgroundColor: 'transparent',
                                      borderColor: '#FF894F',
                                      color: '#FF894F',
                                      borderRadius: '6px',
                                      fontWeight: '500',
                                      height: '24px'
                                    }}
                                    ghost
                                    title="基于深度思考分析结果创建关联订单"
                                  >
                                    关联订单
                                  </Button>
                                  <Button
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={() => openEditThinkingLLMDrawer(record)}
                                    title="编辑深度思考分析结果"
                                    type="primary"
                                    ghost
                                  >
                                    编辑
                                  </Button>
                                </div>
                              </div>
                              <div style={{
                                whiteSpace: 'pre-wrap',
                                background: '#f9f0ff',
                                padding: '12px',
                                borderRadius: '6px',
                                border: '1px solid #d3adf7',
                                lineHeight: '1.6',
                                color: '#262626'
                              }}>
                                {formatFullWidthPunctuation(record.llmThinkingResult)}
                              </div>
                              {(record.tarotImageUrl || record.tarotImagePath) && (
                                <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                  <Button
                                    size="small"
                                    icon={<EyeOutlined />}
                                    onClick={() => handlePreviewCompositeImage(record, true)}
                                    loading={isGeneratingCompositeImage(record, true, generatingComposite)}
                                    type="primary"
                                    ghost
                                  >
                                    {isGeneratingCompositeImage(record, true, generatingComposite) ? '生成中...' : '预览合成图'}
                                  </Button>
                                  <Button
                                    size="small"
                                    icon={<DownloadOutlined />}
                                    onClick={() => handleDownloadCompositeImage(record, true)}
                                    loading={isGeneratingCompositeImage(record, true, generatingComposite)}
                                  >
                                    下载
                                  </Button>
                                  <Button
                                    size="small"
                                    icon={<CopyOutlined />}
                                    onClick={() => handleCopyCompositeImage(record, true)}
                                    title="复制图片到剪贴板"
                                    loading={isGeneratingCompositeImage(record, true, generatingComposite)}
                                  >
                                    复制
                                  </Button>
                                  <Button
                                    size="small"
                                    icon={<FilePdfOutlined />}
                                    onClick={() => handlePreviewCompositePdf(record, true)}
                                    loading={isGeneratingPdfDocument(record, true, generatingPdf)}
                                    type="primary"
                                    ghost
                                  >
                                    {isGeneratingPdfDocument(record, true, generatingPdf) ? '生成PDF...' : '预览PDF'}
                                  </Button>
                                  <Button
                                    size="small"
                                    icon={<FilePdfOutlined />}
                                    onClick={() => handleDownloadCompositePdf(record, true)}
                                    loading={isGeneratingPdfDocument(record, true, generatingPdf)}
                                  >
                                    下载PDF
                                  </Button>
                                </div>
                              )}
                            </div>
                          )}

                          {!record.llmResult && !record.llmThinkingResult && (
                            <div style={{
                              color: '#8c8c8c',
                              fontStyle: 'italic',
                              textAlign: 'center',
                              padding: '16px',
                              background: '#fafafa',
                              borderRadius: '6px'
                            }}>
                              AI分析处理中...
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </List.Item>
          );
        }}
      />

      <Drawer
        title={
          editingRecord
            ? '编辑记录'
            : showTemplateSelection
              ? '选择模板'
              : showInputTypeSelection
                ? '选择录入方式'
                : showImageInput
                  ? '图片录入'
                  : selectedTemplate
                    ? `新增记录 - ${selectedTemplate.name}`
                    : '新增记录'
        }
        width={600}
        onClose={() => {
          setDrawerVisible(false);
          setShowTemplateSelection(false);
          setShowInputTypeSelection(false);
          setShowImageInput(false);
          setSelectedTemplate(null);
          setImageFile(null);
          setExtractedData(null);
          setRelatedOrderData(null); // 清理关联订单数据
          setEditingRecord(null);
          setTarotImageFile(null);
          form.resetFields(); // 重置表单，清除所有数据
        }}
        visible={drawerVisible}
        bodyStyle={{ paddingBottom: 80 }}
        footer={
          (showTemplateSelection || showInputTypeSelection || showImageInput) ? null : (
            <div style={{ textAlign: 'right' }}>
              {!editingRecord && selectedTemplate && (
                <Button
                  onClick={goBackToTemplateSelection}
                  style={{ marginRight: 8 }}
                >
                  返回模板选择
                </Button>
              )}
              <Button
                onClick={() => {
                  setDrawerVisible(false);
                  setShowTemplateSelection(false);
                  setShowInputTypeSelection(false);
                  setShowImageInput(false);
                  setSelectedTemplate(null);
                  setImageFile(null);
                  setExtractedData(null);
                  setRelatedOrderData(null); // 清理关联订单数据
                  setEditingRecord(null);
                  setTarotImageFile(null);
                  form.resetFields(); // 重置表单，清除所有数据
                }}
                style={{ marginRight: 8 }}
              >
                取消
              </Button>
              <Button
                onClick={async () => {
                  // 草稿保存不需要完整表单验证，直接获取当前表单值
                  const values = form.getFieldsValue();
                  handleSaveDraft(values);
                }}
                style={{ marginRight: 8 }}
              >
                保存草稿
              </Button>
              <Button
                type="primary"
                onClick={() => form.submit()}
                disabled={!formValid}
              >
                {editingRecord ? '更新' : '提交'}
              </Button>
            </div>
          )
        }
      >
        {showTemplateSelection ? (
          // 模板选择界面 - 按牌数量分类显示
          <div style={{ padding: '20px 0' }}>
            <div style={{ marginBottom: '20px', textAlign: 'center', color: '#666' }}>
              请选择一个模板来创建新记录
            </div>
            
            {/* 按牌数量分组显示模板 */}
            {(() => {
              // 按牌数量对模板进行分组
              const getCardCount = (templateId: string): number => {
                const cardCount = TemplateManager.getCardCount(templateId);
                if (cardCount === undefined) {
                  return 1; // 默认值
                }
                // 检查是否为特殊分类模板，返回 -1 用于单独分类
                if (TemplateManager.isSpecialCategory(templateId, 'non-specific')) {
                  return -1;
                }
                return cardCount;
              };

              const fourSeasonsTemplates = templates.filter(template =>
                TemplateManager.isSpecialCategory(template.id, 'four-seasons')
              );
              const templatesForGrouping = templates.filter(template =>
                !TemplateManager.isSpecialCategory(template.id, 'four-seasons')
              );

              // 获取分类名称
              const getCategoryName = (cardCount: number): string => {
                if (cardCount === -1) {
                  return '非特定模板';
                }
                return `${cardCount} 张牌`;
              };

              // 对模板按牌数分组
              const templatesByCardCount = templatesForGrouping.reduce((acc, template) => {
                const cardCount = getCardCount(template.id);
                if (!acc[cardCount]) {
                  acc[cardCount] = [];
                }
                acc[cardCount].push(template);
                return acc;
              }, {} as Record<number, Template[]>);

              // 按牌数排序，非特定模板放在最后
              const sortedCardCounts = Object.keys(templatesByCardCount)
                .map(Number)
                .sort((a, b) => {
                  if (a === -1) return 1; // 非特定模板排在最后
                  if (b === -1) return -1;
                  return a - b; // 其他按牌数升序排列
                });

              const sections: JSX.Element[] = [];

              if (fourSeasonsTemplates.length > 0) {
                sections.push(
                  <div key="four-seasons" style={{ marginBottom: '32px' }}>
                    <div style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#262626',
                      marginBottom: '16px',
                      paddingBottom: '8px',
                      borderBottom: '2px solid #f0f0f0',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <span style={{
                        backgroundColor: '#fa8c16',
                        color: '#fff',
                        padding: '4px 12px',
                        borderRadius: '16px',
                        fontSize: '14px',
                        marginRight: '12px',
                        fontWeight: 'bold'
                      }}>
                        四季牌阵
                      </span>
                      <span style={{ color: '#8c8c8c', fontSize: '14px' }}>
                        共 {fourSeasonsTemplates.length} 个模板
                      </span>
                    </div>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                      gap: '16px',
                      paddingLeft: '16px'
                    }}>
                      {fourSeasonsTemplates.map(template => (
                        <div
                          key={template.id}
                          style={{
                            border: '1px solid #d9d9d9',
                            borderRadius: '12px',
                            padding: '16px 8px',
                            textAlign: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            backgroundColor: '#fafafa',
                            height: '110px',
                            justifyContent: 'space-between'
                          }}
                          onClick={() => selectTemplate(template)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = '#fa8c16';
                            e.currentTarget.style.backgroundColor = '#fff7e6';
                            e.currentTarget.style.boxShadow = '0 6px 16px rgba(250, 140, 22, 0.2)';
                            e.currentTarget.style.transform = 'translateY(-3px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = '#d9d9d9';
                            e.currentTarget.style.backgroundColor = '#fafafa';
                            e.currentTarget.style.boxShadow = 'none';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          <div style={{ marginBottom: '8px' }}>
                            {template.icon}
                          </div>
                          <div style={{
                            fontWeight: 'bold',
                            fontSize: '12px',
                            color: '#262626',
                            lineHeight: '1.3',
                            textAlign: 'center'
                          }}>
                            {template.name}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }

              const groupedSections = sortedCardCounts.map(cardCount => (
                <div key={cardCount} style={{ marginBottom: '32px' }}>
                  {/* 分类标题 */}
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#262626',
                    marginBottom: '16px',
                    paddingBottom: '8px',
                    borderBottom: '2px solid #f0f0f0',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <span style={{
                      backgroundColor: cardCount === -1 ? '#52c41a' : '#1890ff',
                      color: '#fff',
                      padding: '4px 12px',
                      borderRadius: '16px',
                      fontSize: '14px',
                      marginRight: '12px',
                      fontWeight: 'bold'
                    }}>
                      {getCategoryName(cardCount)}
                    </span>
                    <span style={{ color: '#8c8c8c', fontSize: '14px' }}>
                      共 {templatesByCardCount[cardCount].length} 个模板
                    </span>
                  </div>

                  {/* 模板网格 */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                    gap: '16px',
                    paddingLeft: '16px'
                  }}>
                    {templatesByCardCount[cardCount].map(template => (
                      <div
                        key={template.id}
                        style={{
                          border: '1px solid #d9d9d9',
                          borderRadius: '12px',
                          padding: '16px 8px',
                          textAlign: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          backgroundColor: '#fafafa',
                          height: '110px',
                          justifyContent: 'space-between'
                        }}
                        onClick={() => selectTemplate(template)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = '#1890ff';
                          e.currentTarget.style.backgroundColor = '#e6f7ff';
                          e.currentTarget.style.boxShadow = '0 6px 16px rgba(24, 144, 255, 0.2)';
                          e.currentTarget.style.transform = 'translateY(-3px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = '#d9d9d9';
                          e.currentTarget.style.backgroundColor = '#fafafa';
                          e.currentTarget.style.boxShadow = 'none';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        <div style={{ marginBottom: '8px' }}>
                          {template.icon}
                        </div>
                        <div style={{ 
                          fontWeight: 'bold', 
                          fontSize: '12px', 
                          color: '#262626',
                          lineHeight: '1.3',
                          textAlign: 'center'
                      }}>
                          {template.name}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ));

              sections.push(...groupedSections);

              return sections;
            })()}
            
            <div style={{ marginTop: '20px', textAlign: 'center', fontSize: '12px', color: '#999' }}>
              点击模板图标开始创建记录
            </div>
          </div>
        ) : showInputTypeSelection ? (
          // 录入方式选择界面
          <div style={{ padding: '40px 20px', textAlign: 'center' }}>
            <h3 style={{ marginBottom: '30px', color: '#333' }}>请选择录入方式</h3>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '30px' }}>
              <div
                onClick={() => handleInputTypeSelection('regular')}
                style={{
                  width: '150px',
                  height: '120px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#fafafa',
                  border: '2px solid #d9d9d9',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#e6f7ff';
                  e.currentTarget.style.borderColor = '#1890ff';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(24, 144, 255, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#fafafa';
                  e.currentTarget.style.borderColor = '#d9d9d9';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <EditOutlined style={{ fontSize: '32px', color: '#1890ff', marginBottom: '10px' }} />
                <span style={{ fontSize: '14px', fontWeight: 'bold' }}>常规录入</span>
                <span style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>手动输入信息</span>
              </div>
              
              <div
                onClick={() => handleInputTypeSelection('image')}
                style={{
                  width: '150px',
                  height: '120px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#fafafa',
                  border: '2px solid #d9d9d9',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f6ffed';
                  e.currentTarget.style.borderColor = '#52c41a';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(82, 196, 26, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#fafafa';
                  e.currentTarget.style.borderColor = '#d9d9d9';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <UpOutlined style={{ fontSize: '32px', color: '#52c41a', marginBottom: '10px' }} />
                <span style={{ fontSize: '14px', fontWeight: 'bold' }}>图片录入</span>
                <span style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>上传聊天截图</span>
              </div>
            </div>
            
            <div style={{ marginTop: '30px' }}>
              <Button onClick={goBackToTemplateSelection}>返回模板选择</Button>
            </div>
          </div>
        ) : showImageInput ? (
          // 图片上传界面
          <ImageUploadForm 
            onSubmit={handleImageUpload}
            onBack={goBackToInputTypeSelection}
            processing={processingImage}
          />
        ) : (
          // 表单界面
          <Form
            key={formResetKey} // 使用resetKey强制重新渲染整个表单
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            onValuesChange={(changedValues: any, allValues: any) => {
              // 如果卡牌数量改变，更新状态
              if (changedValues.cardCount !== undefined) {
                const nextCardCount = parseTarotCardCount(changedValues.cardCount);
                setCardCount(nextCardCount || 1);
              }
              
              // 调试：打印表单值变化
              if (changedValues.generateNormal !== undefined || changedValues.generateThinking !== undefined) {
                console.log('🔍 表单值变化:', changedValues);
                console.log('🔍 所有表单值:', {
                  generateNormal: allValues.generateNormal,
                  generateThinking: allValues.generateThinking
                });
              }
              
              checkFormValid();
            }}
          >
            {/* 关联订单的隐藏字段 */}
            <Form.Item name="parentRecordId" hidden>
              <Input type="hidden" />
            </Form.Item>
            <Form.Item name="parentResultType" hidden>
              <Input type="hidden" />
            </Form.Item>
            <Form.Item name="templateId" hidden>
              <Input type="hidden" />
            </Form.Item>

            {getDataFields(selectedTemplate).map(field => {
              const isCustomerDescription = field.name === 'customerDescription';
              const customerDescriptionExtra = isCustomerDescription ? (
                <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Button
                      size="small"
                      disabled={field.disabled}
                      onMouseDown={preventTextAreaBlur}
                      onClick={() => replaceCustomerDescriptionPronoun('他', '她')}
                    >
                      他→她
                    </Button>
                    <Button
                      size="small"
                      disabled={field.disabled}
                      onMouseDown={preventTextAreaBlur}
                      onClick={() => replaceCustomerDescriptionPronoun('她', '他')}
                    >
                      她→他
                    </Button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '8px' }}>
                    {CUSTOMER_DESCRIPTION_SNIPPETS.map(snippet => (
                      <Button
                        key={snippet}
                        size="small"
                        disabled={field.disabled}
                        onMouseDown={preventTextAreaBlur}
                        onClick={() => insertCustomerDescriptionSnippet(snippet)}
                      >
                        {snippet}
                      </Button>
                    ))}
                  </div>
                </div>
              ) : undefined;
              return (
                <Form.Item
                  key={field.name}
                  name={field.name}
                  label={field.label}
                  rules={field.type === 'number'
                    ? [
                        { required: true, message: `请输入${field.label}` },
                        {
                          validator: (_rule: any, value: unknown) => parseTarotCardCount(value) !== null
                            ? Promise.resolve()
                            : Promise.reject(new Error(`请输入 1-${MAX_TAROT_CARD_COUNT} 之间的整数`))
                        }
                      ]
                    : (field.required ? [{ required: true, message: `请输入${field.label}` }] : [])}
                  valuePropName={field.type === 'switch' ? 'checked' : 'value'}
                  initialValue={field.type === 'switch' ? (field.defaultValue !== undefined ? field.defaultValue : false) : field.defaultValue}
                  extra={customerDescriptionExtra}
                >
                  {field.type === 'textarea' ? (
                    <Input.TextArea
                      ref={isCustomerDescription ? customerDescriptionRef : undefined}
                      placeholder={field.placeholder || `请输入${field.label}`}
                      disabled={field.disabled}
                      rows={4}
                      onSelect={isCustomerDescription ? updateCustomerDescriptionSelection : undefined}
                      onClick={isCustomerDescription ? updateCustomerDescriptionSelection : undefined}
                      onKeyUp={isCustomerDescription ? updateCustomerDescriptionSelection : undefined}
                      onFocus={isCustomerDescription ? updateCustomerDescriptionSelection : undefined}
                    />
                  ) : field.type === 'number' ? (
                    <InputNumber
                      placeholder={`请输入${field.label}`}
                      disabled={field.disabled}
                      min={field.min}
                      max={field.max}
                      precision={0}
                      style={{ width: '100%' }}
                    />
                  ) : field.type === 'upload' ? (
                    <Upload
                      listType="picture-card"
                      disabled={field.name === 'tarotImage' ? !!autoDraw : false}
                      fileList={(() => {
                        if (tarotImageFile) {
                          return [tarotImageFile];
                        } else if (editingRecord?.tarotImageUrl || editingRecord?.tarotImagePath) {
                          // 显示已有记录的图片
                          return [{
                            uid: 'existing',
                            name: '已有图片',
                            status: 'done',
                            url: DataService.getTarotImageUrl(editingRecord.tarotImageUrl || editingRecord.tarotImagePath!)
                          }];
                        }
                        return [];
                      })()}
                      beforeUpload={(file: any) => {
                        setTarotImageFile({
                          uid: '-1',
                          name: file.name,
                          status: 'done',
                          url: URL.createObjectURL(file),
                          originFileObj: file,
                        });
                        return false; // 阻止自动上传
                      }}
                      onRemove={(file: any) => {
                        if (file.uid === 'existing') {
                          // 删除已有记录的图片 - 设置一个标记表示要删除已有图片
                          if (editingRecord) {
                            setEditingRecord({
                              ...editingRecord,
                              tarotImageUrl: undefined,
                              tarotImagePath: undefined
                            });
                          }
                        } else {
                          // 删除新上传的图片
                          if (tarotImageFile && tarotImageFile.url) {
                            URL.revokeObjectURL(tarotImageFile.url);
                          }
                          setTarotImageFile(null);
                        }
                      }}
                      onPreview={(file: any) => {
                        handleImagePreview(file.url || '');
                      }}
                      accept="image/*"
                      maxCount={1}
                    >
                      {!tarotImageFile && (
                        <div>
                          <PlusOutlined />
                          <div style={{ marginTop: 8 }}>{autoDraw ? '自动抽牌已开启' : '上传图片'}</div>
                        </div>
                      )}
                    </Upload>
                  ) : field.type === 'switch' ? (
                    <Switch disabled={field.disabled} />
                  ) : field.type === 'select' ? (
                    <Select
                      placeholder={field.placeholder || `请选择${field.label}`}
                      disabled={field.disabled}
                    >
                      {field.options?.map(option => (
                        <Select.Option key={option.value} value={option.value} disabled={option.disabled}>
                          {option.label}
                        </Select.Option>
                      ))}
                    </Select>
                  ) : (
                    <Input
                      placeholder={field.placeholder || `请输入${field.label}`}
                      disabled={field.disabled}
                    />
                  )}
                </Form.Item>
              );
            })}

            {/* 动态生成卡牌字段；自动抽牌时仅保留牌位信息，不渲染大量牌面输入框。 */}
            {autoDraw ? (
              <>
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message={`提交后系统会从完整 78 张牌中不重复抽取 ${cardCount} 张，并为每张牌独立随机正逆位。`}
                  description={hasStoredAutomaticDraw ? '本订单已完成自动抽牌，编辑和重新解读都会保留原牌面。' : undefined}
                />
                {Array.from({ length: cardCount }, (_, index) => {
                  const configuredCardInfos = selectedTemplate
                    ? (TemplateManager.getConfig(selectedTemplate.id)?.fieldDefaults?.cardInfos as string[] || [])
                    : [];
                  return (
                    <Form.Item
                      key={`auto-card-info-${index}`}
                      name={`card_${index}_info`}
                      initialValue={configuredCardInfos[index] || ''}
                      hidden
                    >
                      <Input />
                    </Form.Item>
                  );
                })}
              </>
            ) : Array.from({ length: cardCount }, (_, index) => {
              // 获取模板配置的卡牌信息（用于默认值，没有配置时返回空字符串）
              const getConfiguredCardInfo = (templateId: string, cardIndex: number): string => {
                const config = TemplateManager.getConfig(templateId);
                const cardInfos = config?.fieldDefaults?.cardInfos as string[] || [];
                return cardInfos[cardIndex] || ''; // 没有配置时返回空字符串，不提供fallback
              };

              // 获取牌面名称输入框的提示文本
              const getCardNamePlaceholder = (templateId: string, cardIndex: number): string => {
                // 非特定模板始终显示通用提示
                if (templateId === 'general-yes-no') {
                  return '请输入牌面名称';
                }
                
                const config = TemplateManager.getConfig(templateId);
                const cardPlaceholders = config?.fieldDefaults?.cardPlaceholders as string[] || [];
                return cardPlaceholders[cardIndex] || `请输入第${cardIndex + 1}张牌的牌面名称`;
              };


              const isTemplateDisabled = selectedTemplate ? 
                TemplateManager.isFieldDisabled(selectedTemplate.id, 'cardInfos') : false;

              const getCardInfoPlaceholder = () => {
                if (!selectedTemplate) {
                  return '请输入对应信息';
                }
                
                // 检查是否为不可编辑的字段
                if (TemplateManager.isFieldDisabled(selectedTemplate.id, 'cardInfos')) {
                  return '(此字段不可编辑)';
                }
                
                // 统一的placeholder提示
                return `第${index + 1}张牌的含义`;
              };

              const getDefaultValue = () => {
                if (!selectedTemplate) {
                  return '';
                }
                
                // 只有对于cardInfos字段被禁用的模板才返回默认值，否则留空
                if (TemplateManager.isFieldDisabled(selectedTemplate.id, 'cardInfos')) {
                  return getConfiguredCardInfo(selectedTemplate.id, index);
                }
                
                return '';
              };

              // 创建唯一的字段key，包含模板ID和resetKey确保模板切换时字段能重新渲染
              const uniqueKey = `${selectedTemplate?.id || 'no-template'}-${cardCount}-card-${index}-${formResetKey}`;
              const fieldNameKey = `card_${index}_name`;
              const fieldInfoKey = `card_${index}_info`;

              return (
                <div key={uniqueKey} style={{ marginBottom: 16, padding: 16, border: '1px solid #d9d9d9', borderRadius: 6 }}>
                  <h4>第 {index + 1} 张牌</h4>
                  <Form.Item
                    key={`${uniqueKey}-name`}
                    name={fieldNameKey}
                    label="牌面"
                    rules={[{ required: !autoDraw, message: '请输入牌面' }]}
                  >
                    <Input 
                      placeholder={selectedTemplate ? getCardNamePlaceholder(selectedTemplate.id, index) : '请输入牌面名称'} 
                      disabled={!!autoDraw}
                    />
                  </Form.Item>
                  <Form.Item
                    key={`${uniqueKey}-info`}
                    name={fieldInfoKey}
                    label="对应信息"
                    rules={[{ required: false }]}
                    initialValue={getDefaultValue()}
                  >
                    <Input.TextArea
                      placeholder={getCardInfoPlaceholder()}
                      disabled={isTemplateDisabled}
                      rows={3}
                    />
                  </Form.Item>
                </div>
              );
            })}
          </Form>
        )}
      </Drawer>

      {/* 图片预览Modal */}
      <Modal
        visible={imagePreviewVisible}
        title="图片预览"
        footer={null}
        onCancel={() => setImagePreviewVisible(false)}
        width="90%"
        style={{ top: 20 }}
        centered
      >
        <div style={{ textAlign: 'center' }}>
          <Image
            src={previewImageUrl}
            style={{
              maxWidth: '100%',
              maxHeight: '70vh',
              objectFit: 'contain'
            }}
            preview={false}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYnN"
            onError={() => {
              console.error('图片加载失败:', previewImageUrl);
              message.error('图片加载失败，请检查网络连接');
            }}
            onLoad={() => {
              console.log('图片加载成功:', previewImageUrl);
            }}
          />
        </div>
      </Modal>

      {/* 编辑AI分析结果抽屉 */}
      <Drawer
        title={editingThinkingResult ? "编辑深度思考分析结果" : "编辑AI分析结果"}
        width={600}
        onClose={() => {
          setEditLLMDrawerVisible(false);
          setEditingLLMRecord(null);
          setEditingThinkingResult(false);
          llmEditForm.resetFields();
        }}
        visible={editLLMDrawerVisible}
        bodyStyle={{ paddingBottom: 80 }}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Button
              onClick={() => {
                setEditLLMDrawerVisible(false);
                setEditingLLMRecord(null);
                setEditingThinkingResult(false);
                llmEditForm.resetFields();
              }}
              style={{ marginRight: 8 }}
            >
              取消
            </Button>
            <Button
              type="primary"
              onClick={() => llmEditForm.submit()}
            >
              保存
            </Button>
          </div>
        }
      >
        <Form
          form={llmEditForm}
          layout="vertical"
          onFinish={handleSaveLLMResult}
        >
          <Form.Item
            name="llmResult"
            label={editingThinkingResult ? "深度思考分析结果" : "AI分析结果"}
            rules={[{ required: true, message: `请输入${editingThinkingResult ? '深度思考分析结果' : 'AI分析结果'}` }]}
          >
            <Input.TextArea
              rows={20}
              placeholder="请输入AI分析结果..."
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>
        </Form>
      </Drawer>

      {/* 统计模态框 */}
      <Modal
        title="数据统计"
        open={statisticsModalVisible}
        onCancel={() => {
          setStatisticsModalVisible(false);
          setStatisticsResult('');
          statisticsForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={statisticsForm}
          layout="vertical"
          onFinish={handleStatistics}
          style={{ marginBottom: 16 }}
        >
          <Form.Item
            name="startDateTime"
            label="开始时间"
            rules={[{ required: true, message: '请选择开始时间' }]}
          >
            <Input
              type="datetime-local"
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                fontSize: '14px'
              }}
              placeholder="选择开始时间"
            />
          </Form.Item>

          <Form.Item
            name="endDateTime"
            label="结束时间"
            rules={[{ required: true, message: '请选择结束时间' }]}
          >
            <Input
              type="datetime-local"
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                fontSize: '14px'
              }}
              placeholder="选择结束时间"
            />
          </Form.Item>
          <Form.Item>
            <Space style={{ width: '100%' }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={statisticsLoading}
                style={{ flex: 1 }}
              >
                统计
              </Button>
              {(user?.role === 'admin' || user?.role === 'controller' || user?.role === 'worker') && (
                <Button
                  type="default"
                  icon={<DownloadOutlined />}
                  loading={csvExportLoading}
                  onClick={handleExportCSV}
                >
                  导出CSV
                </Button>
              )}
            </Space>
          </Form.Item>
        </Form>

        {statisticsResult && (
          <div style={{
            marginTop: 16,
            padding: 16,
            backgroundColor: '#f6f8fa',
            borderRadius: 8,
            border: '1px solid #e1e4e8'
          }}>
            <div style={{
              fontSize: 16,
              fontWeight: 500,
              marginBottom: 12,
              color: '#24292e'
            }}>
              统计结果：
            </div>
            <div style={{
              fontSize: 14,
              lineHeight: 1.6,
              color: '#586069',
              marginBottom: 12
            }}>
              {statisticsResult}
            </div>
            <Button
              type="primary"
              icon={<CopyOutlined />}
              onClick={handleCopyStatisticsResult}
              size="small"
            >
              复制结果
            </Button>
          </div>
        )}
      </Modal>

      {/* 分页组件 */}
      <div style={{ marginTop: 16, textAlign: 'center' }}>
        <Pagination
          current={pagination.current}
          pageSize={pagination.pageSize}
          total={pagination.total}
          onChange={handlePageChange}
          onShowSizeChange={handlePageChange}
          showSizeChanger
          showQuickJumper
          showTotal={(total: number, range: [number, number]) =>
            `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`
          }
          pageSizeOptions={['10', '20', '50', '100']}
        />
      </div>
    </div>
  );

  // 准备标签页数据
  const tabItems = [
    {
      key: 'records',
      label: '数据记录',
      children: renderDataRecords(),
    },
  ];

  // 如果是管理员，添加组织概览标签页
  if (user.role === 'admin') {
    tabItems.push({
      key: 'organization-overview',
      label: '组织概览',
      children: <OrganizationOverview user={user} />,
    });
  }

  // 如果是控制者，仅添加组织管理标签页（用户管理与邀请码管理并入组织管理详情）
  if (user.role === 'controller') {
    tabItems.push(
      {
        key: 'organizations',
        label: '组织管理',
        children: <OrganizationManagement />,
      }
    );
  }

  return (
    <div>
      <Tabs
        defaultActiveKey="records"
        items={tabItems}
        size="large"
      />
    </div>
  );
};

export default Dashboard;
